/**
 * Customer-facing repair intake form — /book-repair
 *
 * Flow:
 *   Pre-step: Pick location (San Diego / Temecula / Mail-In)
 *   Step 1 — Services:           category → model → service list
 *   Step 2 — Add-ons:            curated accessories (static, not sheet-driven)
 *   Step 3 — Controller Bundle:  PS5/Xbox only — TMR upgrade or custom build (10% off)
 *   Step 4 — Contact Info:       name, phone, email, preferred date/time
 *   Step 5 — Review & Submit
 *   Confirmation screen
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MapPin, Phone, ShoppingCart, X, Check, Package,
  ArrowLeft, ArrowRight, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  CheckCircle2, Search, AlertCircle, Clock,
  User, FileText, Truck, Zap, Star, Tag, Gamepad2, CalendarDays,
  DollarSign, RotateCcw,
} from '../lib/icons';
import type { ServiceEntry, DeviceCategory } from '../types/services';
import { getServices, getLastServiceStats, triggerSheetSync } from '../lib/services-store';
import {
  BUYBACK_CATEGORIES, calculateOffer, CONDITIONS,
  type Condition as BuybackCondition,
} from '../data/buyback-pricing';
import { getCachedSheetData, isCacheFresh, SHEETS_CACHE_KEY, syncAgeLabel } from '../lib/sheets-sync';
import { STABLE_DEVICE_MODELS, getStableModels, hasStableModels } from '../data/device-models';
import SEOHead from '../components/SEOHead';
import ImageSlot from '../components/ImageSlot';

// ── Types ──────────────────────────────────────────────────────────────────────

type LocationId    = 'san-diego' | 'temecula' | 'mail-in';
type StepId        = 'services' | 'addons' | 'controller' | 'contact' | 'review';
type RepairSegment = 'gaming' | 'phone';

interface LocationInfo {
  id: LocationId;
  name: string;
  address: string;
  phone: string;
  mapUrl: string;
  emoji: string;
}

interface CartItem {
  service: ServiceEntry;
  quantity: number;
  selectedModelId?: string | null;
}

/** Curated accessory / add-on (not sheet-driven) */
interface AddonItem {
  id: string;
  name: string;
  description: string;
  price: number | null;
  /** Standalone retail price — shown with strikethrough to highlight bundle savings */
  originalPrice?: number | null;
  /** Sheet-sourced compatible_models — used for server-side model compatibility validation */
  deviceModels?: string[];
}

/** Controller bundle item — TMR upgrade or custom build category */
interface BundleItem {
  id: string;
  name: string;
  description: string;
  originalPrice: number | null;
  discountPct: number;
  finalPrice: number | null;
  type: 'tmr' | 'build';
}

interface ContactInfo {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  preferredDate: string;
  preferredTime: string;
  deviceModel: string;
  notes: string;
}

interface ShippingAddress {
  fullName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

interface ShippingOption {
  id: string;
  name: string;
  price: number | null;
  displayPrice: string;
  estimatedDelivery: string;
}

interface SubmitPayload {
  customer: { first_name: string; last_name: string; phone: string; email: string };
  location: { location_id: string; location_name: string; address: string };
  appointment: { preferred_date: string; preferred_time: string; type: 'dropoff' | 'mail-in' };
  selected_services: Array<{
    service_id: string; service_name: string; category: string;
    quantity: number; price: number | null; turnaround_time: string; what_to_bring: string;
    device_models?: string[];
  }>;
  selected_addons: Array<{ addon_id: string; addon_name: string; price: number | null; device_models?: string[] }>;
  selected_bundle: Array<{ bundle_id: string; bundle_name: string; original_price: number | null; final_price: number | null; discount_pct: number }>;
  estimate: { services_subtotal: number; addons_subtotal: number; bundle_subtotal: number; bundle_savings: number; estimated_total: number };
  repairshopr: { subject: string; notes: string; comment: string };
  customer_notes: string;
  shipping_address?: { full_name: string; address1: string; address2: string; city: string; state: string; zip: string };
  shipping_option?: { id: string; name: string; price: number | null; display_price: string; estimated_delivery: string };
}

interface BuybackItem {
  id: string;
  categoryId: string;
  categoryName: string;
  emoji: string;
  modelId: string;
  modelName: string;
  condition: BuybackCondition;
  conditionLabel: string;
  iCloudUnlocked: boolean | null;
  blacklistClean: boolean | null;
  offerValue: number;
}

// ── Constants — Locations ──────────────────────────────────────────────────────

const LOCATIONS: Record<LocationId, LocationInfo> = {
  'san-diego': {
    id: 'san-diego',
    name: 'San Diego',
    address: '5375 Napa St, San Diego, CA 92110',
    phone: '(760) 214-7244',
    mapUrl: 'https://maps.app.goo.gl/UDqXNnSx6yrmCJpw9',
    emoji: '🏄',
  },
  'temecula': {
    id: 'temecula',
    name: 'Temecula',
    address: 'Mobile within Temecula Valley',
    phone: '(951) 216-8534',
    mapUrl: 'https://maps.app.goo.gl/bbZsQWtNbftwZ3ZP8',
    emoji: '🍇',
  },
  'mail-in': {
    id: 'mail-in',
    name: 'Mail-In Repair',
    address: 'Ship to GameFix Solutions',
    phone: '(951) 216-8534',
    mapUrl: '',
    emoji: '📦',
  },
};

// ── Constants — Time Slots ─────────────────────────────────────────────────────

const ALL_TIME_SLOTS: { label: string; h: number; m: number }[] = [
  { label: '9:00 AM',  h: 9,  m: 0  },
  { label: '9:30 AM',  h: 9,  m: 30 },
  { label: '10:00 AM', h: 10, m: 0  },
  { label: '10:30 AM', h: 10, m: 30 },
  { label: '11:00 AM', h: 11, m: 0  },
  { label: '11:30 AM', h: 11, m: 30 },
  { label: '12:00 PM', h: 12, m: 0  },
  { label: '12:30 PM', h: 12, m: 30 },
  { label: '1:00 PM',  h: 13, m: 0  },
  { label: '1:30 PM',  h: 13, m: 30 },
  { label: '2:00 PM',  h: 14, m: 0  },
  { label: '2:30 PM',  h: 14, m: 30 },
  { label: '3:00 PM',  h: 15, m: 0  },
  { label: '3:30 PM',  h: 15, m: 30 },
  { label: '4:00 PM',  h: 16, m: 0  },
  { label: '4:30 PM',  h: 16, m: 30 },
  { label: '5:00 PM',  h: 17, m: 0  },
  { label: '5:30 PM',  h: 17, m: 30 },
  { label: '6:00 PM',  h: 18, m: 0  },
  { label: '6:30 PM',  h: 18, m: 30 },
  { label: '7:00 PM',  h: 19, m: 0  },
  { label: '7:30 PM',  h: 19, m: 30 },
  { label: '8:00 PM',  h: 20, m: 0  },
];

const FLEXIBLE_SLOT = 'Flexible — drop off anytime during business hours';

const seededRandom = (seed: string): number => {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) / 0xffffffff);
};

const getAvailableTimeSlots = (selectedDate: string): {
  available: string[];
  booked: string[];
  noSlotsToday: boolean;
} => {
  // Use LOCAL date string — toISOString() returns UTC which mismatches the
  // date picker (local) causing evening Pacific users to see tomorrow treated as today.
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isToday = selectedDate === today;

  let candidates = ALL_TIME_SLOTS;
  if (isToday) {
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const earliest = Math.ceil(nowMins / 30) * 30 + 90;
    candidates = ALL_TIME_SLOTS.filter(s => s.h * 60 + s.m >= earliest);
  }

  const dateKey = selectedDate || 'no-date';
  const available: string[] = [];
  const booked: string[] = [];

  candidates.forEach(s => {
    const r = seededRandom(`${dateKey}:${s.label}`);
    if (r > 0.25) available.push(s.label);
    else booked.push(s.label);
  });

  if (!isToday && selectedDate) available.push(FLEXIBLE_SLOT);

  return { available, booked, noSlotsToday: isToday && available.length === 0 };
};

// ── Constants — Add-ons (curated, static) ─────────────────────────────────────

const ADDON_HDMI_CABLE: AddonItem = {
  id: 'addon_hdmi_cable',
  name: 'HDMI Cable 6ft',
  description: 'High-speed 4K-capable HDMI cable — leave a spare.',
  price: 20,
};
const ADDON_HDMI_BREAKAWAY: AddonItem = {
  id: 'addon_hdmi_breakaway',
  name: 'Magnetic HDMI Breakaway Adapter',
  description: 'Protects your new HDMI port — magnet releases before a yank damages it.',
  price: 20,
};
// ── Constants — Controller Bundle (PS5/Xbox only) ─────────────────────────────

interface TmrOption {
  id: string;
  name: string;
  tagline: string;
  description: string;
  /** Sheet-authoritative price when serviceId is set; otherwise this hardcoded value is used. */
  originalPrice: number;
  discountPct: number;
  /** ID of the matching ServiceEntry in allServices. Null = no sheet service yet. */
  serviceId: string | null;
}

const TMR_OPTIONS: TmrOption[] = [
  {
    id: 'bundle_tmr_fast',
    name: 'TMR Joystick Upgrade',
    tagline: 'Permanently eliminates drift',
    description: 'Hall effect joysticks replace potentiometer sticks. No drift ever. Done alongside your console repair.',
    originalPrice: 75,
    discountPct: 10,
    serviceId: null, // No standalone TMR sheet service yet — price comes from sheet when added
  },
];

interface BuildCategory {
  id: string;
  name: string;
  benefit: string;
  detail: string;
  /** Reference price only — real price comes from the Google Sheet via serviceId. */
  startingAt: number | null;
  /** ID of the matching ServiceEntry in allServices. Sheet price overrides startingAt. */
  serviceId: string | null;
}

const CONTROLLER_BUILD_CATEGORIES: BuildCategory[] = [
  { id: 'build_tmr',           name: 'TMR Joysticks',                    benefit: 'Hall effect — no drift, ever',       detail: 'Replaces potentiometers with magnetic sensors.',                     startingAt: 75,   serviceId: null               },
  { id: 'build_triggers',      name: 'Clicky Triggers & Bumpers',         benefit: 'Crisp, tactile trigger feel',        detail: 'Mechanical switch upgrade for LT/RT and LB/RB.',                   startingAt: 60,   serviceId: 'ctrl-clicky-trig' },
  { id: 'build_buttons',       name: 'Clicky Face Buttons & D-Pad',       benefit: 'Responsive and satisfying',          detail: 'Tactile switch swap on ABXY and directional pad.',                  startingAt: 50,   serviceId: 'ctrl-clicky-btn'  },
  { id: 'build_triggers_btns', name: 'Clicky Triggers + Buttons Combo',   benefit: 'Full clicky upgrade — save $20',     detail: 'LT/RT/LB/RB + ABXY/D-Pad all done together at a combo rate.',      startingAt: 90,   serviceId: null               },
  { id: 'build_paddles',       name: 'Back Paddles',                      benefit: 'Extra inputs, faster reactions',     detail: 'Standard paddles $80 · Pro 4-paddle module $120.',                  startingAt: 80,   serviceId: 'ctrl-paddles'     },
  { id: 'build_shell',         name: 'Custom Shell & Buttons',             benefit: 'Make it completely yours',           detail: 'Full shell swap — choose any color or graphic combo.',              startingAt: 150,  serviceId: 'ctrl-shells'      },
  { id: 'build_rumble',        name: 'Rumble Delete',                      benefit: 'Lighter, quieter, no vibration',    detail: 'Remove rumble motors to cut weight and noise.',                     startingAt: 25,   serviceId: null               },
  { id: 'build_consult',       name: 'Performance Build Consultation',     benefit: 'Full custom build — your vision',   detail: "Tell us what you want and we'll quote the full build.",             startingAt: null, serviceId: null               },
];

// ── Controller gallery images (shown randomly in the bundle step) ─────────────

const GALLERY_STANDARD_IMGS = [
  '/cammo.JPEG',
  '/forest_green_.JPEG',
  '/yellow.JPEG',
  '/wood_grain.JPEG',
  '/matte_red,_gold_trim.JPEG',
  '/retro_30th_aniversary_.JPEG',
  '/retro_super_station.jpeg',
];

const GALLERY_GRAPHIC_IMGS = [
  '/dualsensecustomgraphic-astroburger.jpg',
  '/dualsensecustomgraphic-bear.jpg',
  '/dualsensecustomgraphic-cottoncandy.jpg',
  '/dualsensecustomgraphic-digitalvenom.jpg',
  '/dualsensecustomgraphic-golddragon.jpg',
  '/dualsensecustomgraphic-lightning.jpg',
  '/dualsensecustomgraphic-milesmorales.jpg',
  '/dualsensecustomgraphic-patriot.jpg',
  '/dualsensecustomgraphic-skull.jpg',
  '/dualsensecustomgraphic-spider.jpg',
  '/dualsensecustomgraphic-vangogh.jpg',
  '/dualsensecustomgraphic-whitedragon.jpg',
];

// ── Constants — Device Categories ─────────────────────────────────────────────

const BLANK_CONTACT: ContactInfo = {
  firstName: '', lastName: '', phone: '', email: '',
  preferredDate: '', preferredTime: '', deviceModel: '', notes: '',
};

const BLANK_SHIPPING: ShippingAddress = {
  fullName: '', address1: '', address2: '', city: '', state: '', zip: '',
};

// ── Mail-In Config ────────────────────────────────────────────────────────────
// Update these values when the shipping address or return rates change.
const MAILIN_SHIP_TO = {
  name:     'GameFix Solutions',
  attn:     'Repair Department',
  address1: '30401 Cupeno Ln',
  city:     'Temecula',
  state:    'CA',
  zip:      '92592',
  phone:    '(951) 216-8534',
};

const SHIPPING_OPTIONS: ShippingOption[] = [
  { id: 'usps-ground',   name: 'USPS Ground Advantage', price: 0,    displayPrice: 'Free',     estimatedDelivery: '2–5 business days' },
  { id: 'usps-priority', name: 'USPS Priority Mail',    price: 10,   displayPrice: '$10',      estimatedDelivery: '1–3 business days' },
  { id: 'ups-2day',      name: 'UPS 2-Day',             price: 25,   displayPrice: '$25–45',   estimatedDelivery: '2 business days'   },
  { id: 'overnight',     name: 'Overnight Shipping',    price: 45,   displayPrice: '$45–75',   estimatedDelivery: 'Next business day' },
];

// ── Controller type / brand picker ────────────────────────────────────────────
// Slugs must match compatible_models values used in the Google Sheet.
// Leave compatible_models blank in the sheet = service applies to ALL controllers.
interface CtrlTypeOption { id: string; label: string; emoji: string }

const CONTROLLER_TYPES: CtrlTypeOption[] = [
  { id: 'ps5 dualsense',                  label: 'PS5 DualSense',              emoji: '🎮' },
  { id: 'ps5 dualsense edge',             label: 'PS5 DualSense Edge',          emoji: '🎮' },
  { id: 'ps4 dualshock 4',               label: 'PS4 DualShock 4',             emoji: '🎮' },
  { id: 'xbox wireless controller',       label: 'Xbox Wireless Controller',    emoji: '🟢' },
  { id: 'xbox elite series 2',            label: 'Xbox Elite Series 2',         emoji: '🟢' },
  { id: 'nintendo switch pro controller', label: 'Switch Pro Controller',       emoji: '🕹️' },
  { id: 'joy-con',                        label: 'Joy-Con',                     emoji: '🕹️' },
];

const CONTROLLER_BRANDS: CtrlTypeOption[] = [
  { id: 'battle beaver', label: 'Battle Beaver', emoji: '🎯' },
  { id: 'scuf',          label: 'SCUF',          emoji: '🎯' },
  { id: 'aim',           label: 'AIM Controller',emoji: '🎯' },
  { id: 'hexgaming',     label: 'HexGaming',     emoji: '🎯' },
  { id: 'cinch',         label: 'Cinch',         emoji: '🎯' },
  { id: 'moddedzone',    label: 'ModdedZone',    emoji: '🎯' },
  { id: 'razer',         label: 'Razer',         emoji: '🎯' },
  { id: 'other',         label: 'Other Brand',   emoji: '🎮' },
];

interface CategoryOption {
  id: DeviceCategory;
  label: string;
  emoji: string;
  description: string;
}

const CATEGORY_OPTIONS: CategoryOption[] = [
  { id: 'ps5',               label: 'PlayStation 5',   emoji: '🎮', description: 'PS5 repairs & upgrades' },
  { id: 'xbox',              label: 'Xbox',             emoji: '🟢', description: 'Xbox repairs & upgrades' },
  { id: 'switch',            label: 'Nintendo',         emoji: '🕹️', description: 'Nintendo Switch repairs & upgrades' },
  { id: 'controller',        label: 'Controllers',      emoji: '🎯', description: 'DualSense, Xbox, JoyCon & custom' },
  { id: 'laptop-macbook',    label: 'Laptop / PC',      emoji: '💻', description: 'MacBook & Windows laptops' },
  { id: 'handheld-pc',       label: 'Gaming Handheld',  emoji: '🖥️', description: 'Steam Deck, ROG Ally & more' },
  { id: 'nintendo-handheld', label: 'Retro / Handheld', emoji: '👾', description: 'GBA, DS, PSP & more' },
];


const PHONE_CATEGORY_OPTIONS: CategoryOption[] = [
  { id: 'iphone',         label: 'iPhone',        emoji: '📱', description: 'Screen, battery, charging & more' },
  { id: 'ipad',           label: 'iPad',           emoji: '⬛', description: 'Screen, battery, charging & more' },
  { id: 'samsung',        label: 'Samsung',        emoji: '🔷', description: 'Screen, battery, charging & more' },
  { id: 'laptop-macbook', label: 'MacBook',        emoji: '💻', description: 'Screen, battery, SSD & more' },
  { id: 'other',          label: 'Other Device',   emoji: '📟', description: 'Describe your device & issue' },
];

// ── Phone protection add-ons ───────────────────────────────────────────────────
const ADDON_TEMPERED_GLASS: AddonItem = {
  id: 'phone_tempered_glass',
  name: 'Tempered Glass Screen Protector',
  description: 'Professional tempered glass installation while your device is here. Protect your screen before you leave.',
  price: 20,
};
const ADDON_PRIVACY_GLASS: AddonItem = {
  id: 'phone_privacy_glass',
  name: 'Privacy Tempered Glass',
  description: 'Blocks side-angle viewing. Applied while your device is in service.',
  price: 25,
};
const ADDON_PROT_PRO_ULTRA: AddonItem = {
  id: 'phone_prot_pro_ultra2',
  name: 'ProtectionPro Ultra 2 by Madico',
  description: 'Premium self-healing TPU film — virtually invisible, absorbs impacts, self-heals minor scratches.',
  price: 45,
};
const ADDON_PROT_PRO_MATTE: AddonItem = {
  id: 'phone_prot_pro_matte',
  name: 'ProtectionPro Matte by Madico',
  description: 'Anti-glare self-healing film — eliminates fingerprints and reduces screen glare.',
  price: 45,
};

// ── Console cleaning — service IDs from Google Sheet ──────────────────────────
// These exist in allServices for price lookup but are NOT shown in the service
// selection list — they appear only as add-ons in the add-ons step.
const CLEANING_ADDON_IDS = new Set([
  'ps5_overheat_bundle_addon',
  'xbox_overheat_bundle_addon',
  'ps5_annual_cleaning_plan',
  'xbox_annual_cleaning_plan',
]);

// Groups for Nintendo Switch add-on display in the add-ons step.
const SWITCH_ADDON_GROUP: Record<string, string> = {
  switch_tmr_1_stick:      'Joystick Upgrades',
  switch_tmr_2_sticks:     'Joystick Upgrades',
  switch2_analog_1_stick:  'Joystick Repairs',
  switch2_analog_2_sticks: 'Joystick Repairs',
  switch_tempered_glass:   'Protection',
  antiglare_film:          'Protection',
  switch_clear_skin:       'Protection',
  switch_matte_skin:       'Protection',
  switch_charger:          'Accessories',
  switch_dock:             'Accessories',
};
const SWITCH_ADDON_GROUP_ORDER = ['Joystick Upgrades', 'Joystick Repairs', 'Protection', 'Accessories'];

// Convert a ServiceEntry to the AddonItem shape used by the add-ons step.
function serviceToAddon(svc: ServiceEntry): AddonItem {
  return {
    id: svc.id, name: svc.repairType, description: svc.description, price: getDisplayPrice(svc),
    deviceModels: svc.deviceModels.length > 0 ? svc.deviceModels : undefined,
  };
}

// ── Model name display formatter ──────────────────────────────────────────────
// The Google Sheet stores model slugs in lowercase (e.g. "ps5 slim", "series x").
// This maps them to properly-cased display labels for the UI.
const MODEL_DISPLAY: Record<string, string> = {
  // PlayStation
  'ps5':              'PS5',
  'ps5 slim':         'PS5 Slim',
  'ps5 pro':          'PS5 Pro',
  // Xbox
  'series x':         'Xbox Series X',
  'series s':         'Xbox Series S',
  // Nintendo Switch
  'switch':           'Switch (LCD)',
  'switch oled':      'Switch OLED',
  'switch 2':         'Switch 2',
  // Gaming Handhelds
  'steam deck lcd':   'Steam Deck LCD',
  'steam deck oled':  'Steam Deck OLED',
  'rog ally':         'ROG Ally',
  'rog ally x':       'ROG Ally X',
  // Controllers — slugs must match compatible_models values in the Google Sheet
  'ps5 dualsense':                  'PS5 DualSense',
  'ps5 dualsense edge':             'PS5 DualSense Edge',
  'ps4 dualshock 4':                'PS4 DualShock 4',
  'xbox wireless controller':       'Xbox Wireless Controller',
  'xbox elite series 2':            'Xbox Elite Series 2',
  'nintendo switch pro controller': 'Switch Pro Controller',
  'switch pro controller':          'Switch Pro Controller',  // legacy alias
  'joy-con':                        'Joy-Con',
  'switch joycon':                  'Switch Joy-Con',         // legacy alias
  'scuf reflex':                    'SCUF Reflex',            // legacy alias
  'scuf':                           'SCUF',
  'battle beaver':                  'Battle Beaver',
  'aim':                            'AIM Controller',
  'cinch':                          'Cinch Controller',
  'hex':                            'HexGaming Controller',   // legacy alias
  'hexgaming':                      'HexGaming Controller',
  'moddedzone':                     'ModdedZone',
  'razer':                          'Razer',
  'other':                          'Other / Custom',
};

function formatModelName(raw: string): string {
  const key = raw.toLowerCase().trim();
  return MODEL_DISPLAY[key] ?? raw.replace(/\b[a-z]/g, c => c.toUpperCase());
}

// ── Buyback categories shown inside the booking flow ─────────────────────────
const INLINE_BUYBACK_CATS = BUYBACK_CATEGORIES.filter(c =>
  ['iphone', 'ipad', 'macbook', 'ps5', 'xbox'].includes(c.id),
);

// ── Price helpers ──────────────────────────────────────────────────────────────

// Splits a tier label like "PS5 / PS5 Slim" or "Xbox Series X, Series S" and
// checks if any segment matches the selected model ID.
// Also handles brand-prefixed labels: "Xbox Series X" matches modelId "series x"
// because the segment ends with " series x".
function matchesTierLabel(label: string, modelId: string): boolean {
  const ml = modelId.trim().toLowerCase();
  return label.split(/\s*[,/]\s*/).some(seg => {
    const sl = seg.trim().toLowerCase();
    return sl === ml || sl.endsWith(' ' + ml);
  });
}

const getDisplayPrice = (service: ServiceEntry, modelId?: string | null): number | null => {
  if (service.basePrice !== null) return service.basePrice;
  if (modelId && service.priceTiers.length > 0) {
    const matched = service.priceTiers.find(t => matchesTierLabel(t.label, modelId));
    if (matched) return matched.price;
  }
  const prices = service.priceTiers.map(t => t.price).filter((p): p is number => p !== null);
  return prices.length > 0 ? Math.min(...prices) : null;
};

const getPriceLabel = (service: ServiceEntry, modelId?: string | null): string => {
  const price = getDisplayPrice(service, modelId);
  if (price === null) return 'Get a Quote';
  if (!modelId) {
    const hasMultiTiers = service.priceTiers.filter(t => t.price !== null).length > 1;
    return hasMultiTiers ? `From $${price}` : `$${price}`;
  }
  return `$${price}`;
};

const calcSubtotal = (items: CartItem[]): number =>
  items.reduce((sum, item) => sum + (getDisplayPrice(item.service, item.selectedModelId) ?? 0) * item.quantity, 0);

const hasAnyQuotes = (items: CartItem[]): boolean =>
  items.some(item => getDisplayPrice(item.service) === null);

const calcAddonSubtotal = (items: AddonItem[]): number =>
  items.reduce((sum, item) => sum + (item.price ?? 0), 0);

const calcBundleSubtotal = (items: BundleItem[]): number =>
  items.reduce((sum, item) => sum + (item.finalPrice ?? 0), 0);

const calcBundleSavings = (items: BundleItem[]): number =>
  items.reduce((sum, item) => {
    if (item.originalPrice === null || item.finalPrice === null) return sum;
    return sum + (item.originalPrice - item.finalPrice);
  }, 0);

// ── Build what-to-bring list (cart only — addons no longer have whatToBring) ──

const combineWhatToBring = (cart: CartItem[]): string[] =>
  [...new Set(cart.map(item => item.service.whatToBring).filter(Boolean))];

// ── Build submission payload ───────────────────────────────────────────────────

const buildPayload = (
  location: LocationInfo,
  cart: CartItem[],
  addons: AddonItem[],
  bundleItems: BundleItem[],
  contact: ContactInfo,
  buybackItems: BuybackItem[] = [],
  shippingAddress?: ShippingAddress,
  selectedShipping?: ShippingOption | null,
): SubmitPayload => {
  const servicesSubtotal = calcSubtotal(cart);
  const addonsSubtotal   = calcAddonSubtotal(addons);
  const bundleSubtotal   = calcBundleSubtotal(bundleItems);
  const bundleSavings    = calcBundleSavings(bundleItems);
  const total            = servicesSubtotal + addonsSubtotal + bundleSubtotal;

  const isMailIn  = location.id === 'mail-in';
  const fullName  = `${contact.firstName} ${contact.lastName}`.trim();

  // RS subject: use repairshopr_subject from Sheet (stored in internalNotes) if present,
  // otherwise fall back to service_name. No location or customer name.
  const rsSubjects = cart.map(item => item.service.internalNotes?.trim() || item.service.repairType);
  const subjectParts = rsSubjects.slice(0, 2);
  if (rsSubjects.length > 2) subjectParts.push(`+${rsSubjects.length - 2} more`);
  const subject = subjectParts.join(' + ');

  // RS ticket comment — all booking details for the technician
  const comment: string[] = [];

  if (isMailIn) {
    comment.push('MAIL-IN REPAIR');
    if (shippingAddress) {
      const addr2 = shippingAddress.address2 ? ` ${shippingAddress.address2}` : '';
      comment.push(`Return Address: ${shippingAddress.fullName}, ${shippingAddress.address1}${addr2}, ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip}`);
    }
    if (selectedShipping) {
      comment.push(`Return Shipping: ${selectedShipping.name} (${selectedShipping.displayPrice}) — Est. ${selectedShipping.estimatedDelivery}`);
    }
  } else {
    comment.push('APPOINTMENT');
    comment.push(`Location: ${location.name}`);
    comment.push(`Date/Time: ${contact.preferredDate} at ${contact.preferredTime}`);
  }

  comment.push('', 'SELECTED SERVICES');
  cart.forEach(item => {
    const p = getDisplayPrice(item.service, item.selectedModelId);
    const qty = item.quantity > 1 ? ` x${item.quantity}` : '';
    comment.push(`  - ${item.service.repairType}${qty} — ${p !== null ? `$${p}` : 'Quote'}`);
    if (item.service.description) comment.push(`    ${item.service.description}`);
  });

  if (addons.length > 0) {
    comment.push('', 'ADD-ONS');
    addons.forEach(a => comment.push(`  - ${a.name} — ${a.price !== null ? `$${a.price}` : 'Quote'}`));
  }

  if (bundleItems.length > 0) {
    comment.push('', 'CONTROLLER BUNDLE (10% off)');
    bundleItems.forEach(b => {
      const orig  = b.originalPrice !== null ? `$${b.originalPrice}` : 'TBD';
      const final = b.finalPrice    !== null ? `$${b.finalPrice}`    : 'Quote';
      comment.push(`  - ${b.name} — ${final} (was ${orig})`);
    });
    if (bundleSavings > 0) comment.push(`  Bundle savings: $${bundleSavings.toFixed(2)}`);
  }

  if (buybackItems.length > 0) {
    comment.push('', 'TRADE-IN / BUYBACK INTERESTS');
    buybackItems.forEach(b => {
      const flags = [
        b.iCloudUnlocked === false ? 'iCloud locked' : null,
        b.blacklistClean === false ? 'carrier issue'  : null,
      ].filter(Boolean).join(', ');
      comment.push(`  - ${b.modelName} (${b.conditionLabel}) — Est. $${b.offerValue}${flags ? ` [${flags}]` : ''}`);
    });
    const tradeTotal = buybackItems.reduce((s, b) => s + b.offerValue, 0);
    comment.push(`  Total est. trade-in credit: $${tradeTotal}`);
  }

  comment.push('', `ESTIMATED TOTAL: $${total}${hasAnyQuotes(cart) ? '+' : ''}`);
  comment.push('(Final price confirmed after inspection)');

  if (contact.deviceModel) comment.push('', `Device Model: ${contact.deviceModel}`);

  comment.push('', 'CUSTOMER');
  comment.push(fullName);
  comment.push(contact.phone);
  comment.push(contact.email);

  if (contact.notes) {
    comment.push('', 'CUSTOMER NOTES');
    comment.push(contact.notes);
  }

  const payload: SubmitPayload = {
    customer: { first_name: contact.firstName, last_name: contact.lastName, phone: contact.phone, email: contact.email },
    location: { location_id: location.id, location_name: location.name, address: location.address },
    appointment: { preferred_date: contact.preferredDate, preferred_time: contact.preferredTime, type: isMailIn ? 'mail-in' : 'dropoff' },
    selected_services: cart.map(item => ({
      service_id: item.service.id, service_name: item.service.repairType, category: item.service.deviceCategory,
      quantity: item.quantity, price: getDisplayPrice(item.service, item.selectedModelId),
      turnaround_time: item.service.turnaround, what_to_bring: item.service.whatToBring,
      requires_parent_service: item.service.requiresParentService ?? false,
      service_type: item.service.serviceType ?? 'repair',
      eligible_parent_service_ids: item.service.eligibleParentServiceIds ?? [],
      device_models: item.service.deviceModels,
    })),
    selected_addons: addons.map(a => ({ addon_id: a.id, addon_name: a.name, price: a.price, device_models: a.deviceModels ?? [] })),
    selected_bundle: bundleItems.map(b => ({
      bundle_id: b.id, bundle_name: b.name,
      original_price: b.originalPrice, final_price: b.finalPrice, discount_pct: b.discountPct,
    })),
    estimate: { services_subtotal: servicesSubtotal, addons_subtotal: addonsSubtotal, bundle_subtotal: bundleSubtotal, bundle_savings: bundleSavings, estimated_total: total },
    repairshopr: { subject, notes: contact.notes, comment: comment.join('\n') },
    customer_notes: contact.notes,
  };

  if (isMailIn && shippingAddress) {
    payload.shipping_address = {
      full_name: shippingAddress.fullName,
      address1:  shippingAddress.address1,
      address2:  shippingAddress.address2,
      city:      shippingAddress.city,
      state:     shippingAddress.state,
      zip:       shippingAddress.zip,
    };
  }
  if (isMailIn && selectedShipping) {
    payload.shipping_option = {
      id:                 selectedShipping.id,
      name:               selectedShipping.name,
      price:              selectedShipping.price,
      display_price:      selectedShipping.displayPrice,
      estimated_delivery: selectedShipping.estimatedDelivery,
    };
  }

  return payload;
};

// ── Sub-components ────────────────────────────────────────────────────────────

// Strips references to non-selected models from display text.
// e.g. "HDMI Port for Xbox Series X and Series S" → "HDMI Port for Xbox Series X"
// when selectedModelId = "series x".
// Only acts when the service itself covers multiple models (deviceModels.length > 1).
const CONNECTOR_PATTERN = '(?:\\s*[,/]\\s*|\\s+(?:and|or|&)\\s+)';
function cleanTextForModel(
  text: string,
  selectedModelId: string | null | undefined,
  serviceModels: string[],
): string {
  if (!selectedModelId || serviceModels.length <= 1) return text;
  const selLower = selectedModelId.toLowerCase();
  // Sort longer labels first so "PS5 Slim" is removed before bare "PS5"
  const others = serviceModels
    .filter(m => m.toLowerCase() !== selLower)
    .sort((a, b) => {
      const la = MODEL_DISPLAY[a.toLowerCase()] ?? a;
      const lb = MODEL_DISPLAY[b.toLowerCase()] ?? b;
      return lb.length - la.length;
    });
  if (others.length === 0) return text;

  let result = text;
  for (const modelId of others) {
    const label = MODEL_DISPLAY[modelId.toLowerCase()] ?? modelId;
    const capId = modelId.replace(/\b\w/g, c => c.toUpperCase());
    for (const variant of [...new Set([label, capId])]) {
      const esc = variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result
        .replace(new RegExp(`${CONNECTOR_PATTERN}${esc}`, 'gi'), '')
        .replace(new RegExp(`${esc}${CONNECTOR_PATTERN}`, 'gi'), '');
    }
  }
  return result.trim().replace(/  +/g, ' ');
}

const ServiceRow = ({
  service, inCart, quantity, selectedModelId, onAdd, onIncrease, onDecrease,
}: {
  service: ServiceEntry;
  inCart: boolean;
  quantity: number;
  selectedModelId?: string | null;
  onAdd: () => void;
  onIncrease: () => void;
  onDecrease: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  // Filter price tiers to matching model only — prevents showing "Xbox Series S: $X"
  // when customer already selected Xbox Series X.
  const visibleTiers = useMemo(() =>
    selectedModelId
      ? service.priceTiers.filter(t => matchesTierLabel(t.label, selectedModelId))
      : service.priceTiers,
    [service.priceTiers, selectedModelId],
  );

  // Strip other-model names from display text when a model is already selected.
  const displayName = useMemo(() =>
    cleanTextForModel(service.repairType, selectedModelId, service.deviceModels),
    [service.repairType, selectedModelId, service.deviceModels],
  );
  const displayDesc = useMemo(() =>
    cleanTextForModel(service.description, selectedModelId, service.deviceModels),
    [service.description, selectedModelId, service.deviceModels],
  );

  const hasDetails = !!(service.whatToBring || service.warranty || service.disclaimer || visibleTiers.length > 0);

  return (
    <div className={`transition-colors border-b border-gray-800/60 last:border-b-0 ${inCart ? 'bg-[#39FF14]/[0.03]' : 'hover:bg-gray-900/40'}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div
          className={`flex-1 min-w-0 ${hasDetails ? 'cursor-pointer' : ''}`}
          onClick={hasDetails ? () => setExpanded(e => !e) : undefined}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            {inCart && <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] shrink-0" />}
            <span className={`font-semibold text-sm truncate ${inCart ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>
              {displayName}
            </span>
            {hasDetails && (
              <ChevronDown className={`h-3 w-3 text-gray-600 transition-transform shrink-0 ${expanded ? 'rotate-180' : ''}`} />
            )}
          </div>
          {(displayDesc || service.turnaround) && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {[displayDesc, service.turnaround].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-sm font-bold tabular-nums ${inCart ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>
            {getPriceLabel(service, selectedModelId)}
          </span>
          {inCart ? (
            <div className="flex items-center gap-0.5 bg-gray-800 border border-gray-700 rounded-lg px-1 py-1">
              <button onClick={onDecrease} className="w-6 h-6 flex items-center justify-center text-[#E0E0E0] hover:text-[#FF1744] font-bold text-sm transition-colors rounded">−</button>
              <span className="text-xs font-bold w-4 text-center text-[#E0E0E0]">{quantity}</span>
              <button onClick={onIncrease} className="w-6 h-6 flex items-center justify-center text-[#E0E0E0] hover:text-[#39FF14] font-bold text-sm transition-colors rounded">+</button>
            </div>
          ) : (
            <button onClick={onAdd} className="h-8 px-3 rounded-lg bg-gray-800 hover:bg-[#39FF14] hover:text-[#0D0D0D] text-[#B0B0B0] text-xs font-bold transition-all border border-gray-700 hover:border-[#39FF14]">
              + Add
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pt-1 pb-3 space-y-1.5 border-t border-gray-800/40 bg-gray-950/40">
          {visibleTiers.length > 0 && (
            <div className="space-y-1 mb-1">
              {visibleTiers.map((t, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-gray-400">{t.label}</span>
                  <span className="text-[#E0E0E0] font-semibold">{t.price !== null ? `$${t.price}` : 'Get a Quote'}</span>
                </div>
              ))}
            </div>
          )}
          {service.whatToBring && <p className="text-xs text-gray-500"><span className="text-gray-400 font-medium">What to bring: </span>{service.whatToBring}</p>}
          {service.warranty && <p className="text-xs text-gray-500"><span className="text-gray-400 font-medium">Warranty: </span>{service.warranty}</p>}
          {service.disclaimer && <p className="text-xs text-yellow-500/60">{service.disclaimer}</p>}
        </div>
      )}
    </div>
  );
};

// Progress bar — driven by activeSteps array so the controller step is conditional
const ProgressBar = ({ activeSteps, currentStep }: { activeSteps: { id: StepId; label: string }[]; currentStep: StepId }) => {
  const currentIndex = activeSteps.findIndex(s => s.id === currentStep);
  return (
    <div className="flex items-center gap-0 mb-6">
      {activeSteps.map(({ id, label }, i) => (
        <React.Fragment key={id}>
          <div className="flex items-center gap-1.5 min-w-0">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors
              ${i <= currentIndex ? 'bg-[#39FF14] text-[#0D0D0D]' : 'bg-gray-800 text-gray-500'}`}>
              {i < currentIndex ? <Check className="h-3 w-3" /> : i + 1}
            </div>
            <span className={`text-xs font-medium hidden sm:block transition-colors
              ${i === currentIndex ? 'text-[#E0E0E0]' : i < currentIndex ? 'text-[#39FF14]' : 'text-gray-600'}`}>
              {label}
            </span>
          </div>
          {i < activeSteps.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 transition-colors ${i < currentIndex ? 'bg-[#39FF14]' : 'bg-gray-800'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Compact repair summary panel
const RepairSummary = ({
  cart, addons, bundleItems, buybackItems = [], step,
  onRemove, onQtyChange, onRemoveAddon, onRemoveBundle, onRemoveBuyback, onContinue,
}: {
  cart: CartItem[];
  addons: AddonItem[];
  bundleItems: BundleItem[];
  buybackItems?: BuybackItem[];
  step: StepId;
  onRemove: (id: string) => void;
  onQtyChange: (id: string, qty: number) => void;
  onRemoveAddon: (id: string) => void;
  onRemoveBundle: (id: string) => void;
  onRemoveBuyback?: (id: string) => void;
  onContinue: () => void;
}) => {
  const servicesSubtotal = calcSubtotal(cart);
  const addonsSubtotal   = calcAddonSubtotal(addons);
  const bundleSubtotal   = calcBundleSubtotal(bundleItems);
  const bundleSavings    = calcBundleSavings(bundleItems);
  const total = servicesSubtotal + addonsSubtotal + bundleSubtotal;
  const quotes = hasAnyQuotes(cart);
  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);
  const tradeTotal = buybackItems.reduce((s, b) => s + b.offerValue, 0);

  return (
    <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-800 flex items-center gap-2">
        <ShoppingCart className="h-3.5 w-3.5 text-[#39FF14]" />
        <span className="font-bold text-[#E0E0E0] text-xs uppercase tracking-wide">Your Request</span>
        {totalItems > 0 && (
          <span className="ml-auto text-[10px] bg-[#39FF14] text-[#0D0D0D] px-1.5 py-0.5 rounded-full font-bold tabular-nums">{totalItems}</span>
        )}
      </div>

      {cart.length === 0 ? (
        <div className="px-4 py-8 text-center text-gray-600 text-xs">
          <Package className="h-6 w-6 mx-auto mb-2 opacity-25" />
          No services added yet
        </div>
      ) : (
        <div className="px-4 py-3 space-y-2">
          {/* Service items */}
          {cart.map(item => {
            const price = getDisplayPrice(item.service, item.selectedModelId);
            return (
              <div key={item.service.id} className="flex items-center gap-2 group">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#E0E0E0] font-medium leading-tight truncate">{item.service.repairType}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <button onClick={() => onQtyChange(item.service.id, Math.max(1, item.quantity - 1))} className="w-4 h-4 rounded bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] text-[10px] flex items-center justify-center transition-colors">−</button>
                    <span className="text-[10px] text-gray-500 tabular-nums">×{item.quantity}</span>
                    <button onClick={() => onQtyChange(item.service.id, item.quantity + 1)} className="w-4 h-4 rounded bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] text-[10px] flex items-center justify-center transition-colors">+</button>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-bold text-[#39FF14] tabular-nums">{price !== null ? `$${price * item.quantity}` : '—'}</p>
                  <button onClick={() => onRemove(item.service.id)} className="text-[10px] text-gray-700 hover:text-[#FF1744] transition-colors">×</button>
                </div>
              </div>
            );
          })}

          {/* Add-on items */}
          {addons.length > 0 && (
            <>
              <div className="border-t border-gray-800/60 pt-2">
                <p className="text-[10px] text-gray-600 font-semibold uppercase tracking-wider mb-1.5">Add-ons</p>
              </div>
              {addons.map(item => (
                <div key={item.id} className="flex items-center gap-2">
                  <p className="flex-1 text-xs text-gray-500 truncate">{item.name}</p>
                  <div className="text-right shrink-0 flex items-center gap-2">
                    <p className="text-xs font-semibold text-gray-500 tabular-nums">{item.price !== null ? `$${item.price}` : '—'}</p>
                    <button onClick={() => onRemoveAddon(item.id)} className="text-[10px] text-gray-700 hover:text-[#FF1744] transition-colors">×</button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Bundle items */}
          {bundleItems.length > 0 && (
            <>
              <div className="border-t border-gray-800/60 pt-2">
                <p className="text-[10px] text-[#00BFFF] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <Tag className="h-2.5 w-2.5" /> Controller Bundle
                </p>
              </div>
              {bundleItems.map(item => (
                <div key={item.id} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#00BFFF] truncate">{item.name}</p>
                    {item.originalPrice !== null && item.finalPrice !== null && (
                      <p className="text-[10px] text-gray-600">
                        <span className="line-through">${item.originalPrice}</span>
                        <span className="text-[#39FF14] ml-1">${item.finalPrice}</span>
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0 flex items-center gap-2">
                    <p className="text-xs font-semibold text-[#00BFFF] tabular-nums">
                      {item.finalPrice !== null ? `$${item.finalPrice}` : 'Quote'}
                    </p>
                    <button onClick={() => onRemoveBundle(item.id)} className="text-[10px] text-gray-700 hover:text-[#FF1744] transition-colors">×</button>
                  </div>
                </div>
              ))}
              {bundleSavings > 0 && (
                <p className="text-[10px] text-[#39FF14] font-semibold">You save ${bundleSavings.toFixed(2)} with bundle discount</p>
              )}
            </>
          )}

          {/* Buyback / trade-in credits */}
          {buybackItems.length > 0 && (
            <>
              <div className="border-t border-gray-800/60 pt-2">
                <p className="text-[10px] text-[#39FF14] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <DollarSign className="h-2.5 w-2.5" /> Trade-In Credit
                </p>
              </div>
              {buybackItems.map(b => (
                <div key={b.id} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#39FF14] truncate">{b.emoji} {b.modelName}</p>
                    <p className="text-[10px] text-gray-600">{b.conditionLabel}</p>
                  </div>
                  <div className="text-right shrink-0 flex items-center gap-2">
                    <p className="text-xs font-semibold text-[#39FF14] tabular-nums">~${b.offerValue}</p>
                    {onRemoveBuyback && (
                      <button onClick={() => onRemoveBuyback(b.id)} className="text-[10px] text-gray-700 hover:text-[#FF1744] transition-colors">×</button>
                    )}
                  </div>
                </div>
              ))}
              {tradeTotal > 0 && (
                <p className="text-[10px] text-gray-600 mt-0.5">Est. credit finalized at drop-off.</p>
              )}
            </>
          )}

          {/* Total */}
          <div className="border-t border-gray-800 pt-2 mt-1">
            {(addons.length > 0 || bundleItems.length > 0) && (
              <div className="space-y-0.5 mb-1.5">
                <div className="flex justify-between text-[10px] text-gray-600">
                  <span>Services</span><span>${servicesSubtotal}</span>
                </div>
                {addons.length > 0 && (
                  <div className="flex justify-between text-[10px] text-gray-600">
                    <span>Add-ons</span><span>${addonsSubtotal}</span>
                  </div>
                )}
                {bundleItems.length > 0 && (
                  <div className="flex justify-between text-[10px] text-gray-600">
                    <span>Controller Bundle</span><span>${bundleSubtotal}</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-between items-baseline">
              <span className="text-xs text-gray-400">Estimated Total</span>
              <span className="text-sm font-black text-[#39FF14] tabular-nums">{quotes ? `$${total}+` : `$${total}`}</span>
            </div>
            {tradeTotal > 0 && (
              <div className="flex justify-between items-baseline mt-0.5">
                <span className="text-[10px] text-[#39FF14]/70">Trade-In Est.</span>
                <span className="text-xs font-bold text-[#39FF14]/70 tabular-nums">~-${tradeTotal}</span>
              </div>
            )}
            <p className="text-[10px] text-gray-700 mt-1 leading-relaxed">Final price confirmed after inspection.</p>
          </div>

          {step === 'services' && (
            <button onClick={onContinue}
              className="w-full py-2 rounded-lg bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold text-xs transition-colors flex items-center justify-center gap-1.5 mt-1">
              Continue <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Inline Calendar ───────────────────────────────────────────────────────────
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Converts a local Date to a YYYY-MM-DD string (no UTC shift). */
const toDateStr = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface InlineCalendarProps {
  value: string;       // YYYY-MM-DD or '' (currently selected date)
  min: string;         // YYYY-MM-DD — earliest selectable date (today)
  onChange: (date: string) => void;
  onClose: () => void;
}

const InlineCalendar = ({ value, min, onChange, onClose }: InlineCalendarProps) => {
  // Parse min as a local midnight Date for comparisons
  const [minDate] = React.useState<Date>(() => {
    const [y, m, d] = min.split('-').map(Number);
    return new Date(y, m - 1, d);
  });

  // Start the calendar view on the selected month (or today)
  const initialDate = value
    ? (() => { const [y, m, d] = value.split('-').map(Number); return new Date(y, m - 1, d); })()
    : new Date();
  const [viewYear,  setViewYear]  = React.useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = React.useState(initialDate.getMonth()); // 0-indexed

  const today = React.useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);

  const selectedDate = React.useMemo(() => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [value]);

  const monthLabel = new Date(viewYear, viewMonth, 1)
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Build the grid cells (null = empty padding cell)
  const cells = React.useMemo<(Date | null)[]>(() => {
    const firstDow   = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
    const daysInMon  = new Date(viewYear, viewMonth + 1, 0).getDate();
    const list: (Date | null)[] = Array(firstDow).fill(null);
    for (let d = 1; d <= daysInMon; d++) list.push(new Date(viewYear, viewMonth, d));
    while (list.length % 7 !== 0) list.push(null); // pad to full rows
    return list;
  }, [viewYear, viewMonth]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // Disable back-nav if the entire previous month is before minDate
  const canGoPrev = new Date(viewYear, viewMonth, 0) >= minDate;

  return (
    <div className="mt-2 bg-[#111] border border-[#39FF14]/30 rounded-2xl p-4 shadow-xl">
      {/* ── Month header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={prevMonth}
          disabled={!canGoPrev}
          className="p-2 rounded-lg text-gray-500 hover:text-[#E0E0E0] hover:bg-gray-800 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-bold text-[#E0E0E0] tracking-wide">{monthLabel}</span>
        <button
          type="button"
          onClick={nextMonth}
          className="p-2 rounded-lg text-gray-500 hover:text-[#E0E0E0] hover:bg-gray-800 transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* ── Weekday labels ───────────────────────────────────────────── */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAY_LABELS.map(lbl => (
          <div key={lbl} className="text-center text-[10px] text-gray-600 font-bold uppercase py-1 tracking-wider">
            {lbl}
          </div>
        ))}
      </div>

      {/* ── Day grid ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((date, i) => {
          if (!date) return <div key={`pad-${i}`} className="h-9" />;

          const isPast     = date < minDate;
          const isToday    = date.getTime() === today.getTime();
          const isSelected = !!selectedDate && date.getTime() === selectedDate.getTime();

          return (
            <button
              key={toDateStr(date)}
              type="button"
              disabled={isPast}
              onClick={() => { onChange(toDateStr(date)); onClose(); }}
              className={[
                'h-9 w-full flex items-center justify-center rounded-lg text-sm font-semibold transition-all',
                isPast     ? 'text-gray-700 cursor-not-allowed'                               : '',
                isSelected ? 'bg-[#39FF14] text-[#0D0D0D]'                                   : '',
                !isPast && !isSelected && isToday
                             ? 'ring-1 ring-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14]/10' : '',
                !isPast && !isSelected && !isToday
                             ? 'text-[#E0E0E0] hover:bg-gray-800'                             : '',
              ].join(' ')}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const PHONE_CATEGORIES = new Set<string>(['iphone', 'ipad', 'samsung']);

const BookRepairPage = () => {
  // ── Router state (preselection from RepairSelector) ───────────────────────
  const routerLocation = useLocation();
  const routerState = routerLocation.state as {
    preselectedService?: ServiceEntry;
    preselectedModelId?: string | null;
  } | null;

  // ── State ─────────────────────────────────────────────────────────────────
  const [segment, setSegment] = useState<RepairSegment | null>(() => {
    const svc = routerState?.preselectedService;
    if (!svc) return null;
    return PHONE_CATEGORIES.has(svc.deviceCategory) ? 'phone' : 'gaming';
  });
  const [locationId, setLocationId]   = useState<LocationId | null>(null);
  const [step, setStep]               = useState<StepId>('services');
  const [cart, setCart]               = useState<CartItem[]>(() => {
    const svc = routerState?.preselectedService;
    if (!svc) return [];
    // Reject add-on preselection — a direct link or RepairSelector must not bypass primary service requirement
    if (svc.requiresParentService || svc.isUpsell || svc.serviceType === 'add_on') return [];
    return [{ service: svc, quantity: 1, selectedModelId: routerState?.preselectedModelId ?? null }];
  });
  const [addons, setAddons]           = useState<AddonItem[]>([]);
  const [removedSwitchAddonNames, setRemovedSwitchAddonNames] = useState<string[]>([]);
  const [bundleItems, setBundleItems] = useState<BundleItem[]>([]);
  const [contact, setContact]         = useState<ContactInfo>(BLANK_CONTACT);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>(BLANK_SHIPPING);
  const [selectedShipping, setSelectedShipping] = useState<ShippingOption | null>(null);
  const [errors, setErrors]           = useState<Record<string, string>>({});
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [searchQ, setSearchQ]         = useState('');
  const [submitted, setSubmitted]       = useState<SubmitPayload | null>(null);
  const [submitting, setSubmitting]     = useState(false);
  const [submitError, setSubmitError]   = useState<string | null>(null);
  const [, setRepairshoprFailed] = useState(false);
  const [debugInfo, setDebugInfo] = useState<Record<string, string> | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // ── Buyback / trade-in ────────────────────────────────────────────────────
  const [buybackItems, setBuybackItems] = useState<BuybackItem[]>([]);
  const [bbOpen,    setBbOpen]    = useState(false);
  type BbStep = 'category' | 'model' | 'condition' | 'icloud' | 'carrier' | 'offer';
  const [bbStep,    setBbStep]    = useState<BbStep>('category');
  const [bbCatId,   setBbCatId]   = useState<string | null>(null);
  const [bbModelId, setBbModelId] = useState<string | null>(null);
  const [bbCond,    setBbCond]    = useState<BuybackCondition | null>(null);
  const [bbICloud,  setBbICloud]  = useState<boolean | null>(null);
  const [bbCarrier, setBbCarrier] = useState<boolean | null>(null);

  // Random controller gallery — 1 graphic + 2 standard colors, picked once per session
  const [controllerGalleryImages] = useState<string[]>(() => {
    const graphic  = GALLERY_GRAPHIC_IMGS[Math.floor(Math.random() * GALLERY_GRAPHIC_IMGS.length)];
    const shuffled = [...GALLERY_STANDARD_IMGS].sort(() => Math.random() - 0.5);
    return [graphic, shuffled[0], shuffled[1]];
  });

  const debugMode = useMemo(() => new URLSearchParams(window.location.search).get('debug') === '1', []);

  // Device / model selection sub-steps within 'services'
  type ServiceSubStep = 'category' | 'model' | 'ctrl-type' | 'list' | 'other-form' | 'handheld-family';
  const [serviceSubStep, setServiceSubStep] = useState<ServiceSubStep>(() => {
    const svc = routerState?.preselectedService;
    if (!svc || svc.requiresParentService || svc.isUpsell || svc.serviceType === 'add_on') return 'category';
    return 'list';
  });
  const [selectedCategory, setSelectedCategory] = useState<DeviceCategory | null>(() => {
    const svc = routerState?.preselectedService;
    if (!svc || svc.requiresParentService || svc.isUpsell || svc.serviceType === 'add_on') return null;
    return (svc.deviceCategory as DeviceCategory) ?? null;
  });
  const [selectedModelId, setSelectedModelId] = useState<string | null>(
    () => routerState?.preselectedModelId ?? null
  );
  const [selectedHandheldFamily, setSelectedHandheldFamily] = useState<string | null>(null);
  const [ctrlBrandOpen, setCtrlBrandOpen]       = useState(false);
  // "Other" free-form fields
  const [otherDevice,   setOtherDevice]   = useState('');
  const [otherSymptoms, setOtherSymptoms] = useState('');

  // ── Services from store ───────────────────────────────────────────────────
  // Initialised synchronously from whatever is cached; refreshed after sheet sync.
  const [allServices, setAllServices] = useState<ServiceEntry[]>(() =>
    getServices().filter(s => s.status === 'active'),
  );

  useEffect(() => {
    const cached = getCachedSheetData();
    const alreadyFresh = cached && isCacheFresh(cached.fetchedAt);

    // Helper: find PS5 fan connector regardless of whether data came from
    // the seed (id='ps5-fan-conn') or the sheet (id='ps5_ripped_fan_connector')
    const findFanConn = (list: ServiceEntry[]) =>
      list.find(s => s.id === 'ps5-fan-conn' || s.id === 'ps5_ripped_fan_connector' ||
        /ripped.*fan|fan.*connector/i.test(s.repairType));

    if (alreadyFresh) {
      // Cache is good — re-read so we're guaranteed to use the sheet version
      const fresh = getServices().filter(s => s.status === 'active');
      setAllServices(fresh);
      if (import.meta.env.DEV) {
        const fc = findFanConn(fresh);
        const price = fc ? (fc.basePrice !== null ? `$${fc.basePrice}` : `From $${fc.priceTiers[0]?.price}`) : 'not found';
        console.info(`[GameFix Services] source=google_sheet(fresh) | PS5 Fan Connector id=${fc?.id} price=${price}`);
      }
    } else {
      // Cache stale — trigger a sync then refresh
      triggerSheetSync().then(() => {
        const fresh = getServices().filter(s => s.status === 'active');
        setAllServices(fresh);
        if (import.meta.env.DEV) {
          const fc = findFanConn(fresh);
          const price = fc ? (fc.basePrice !== null ? `$${fc.basePrice}` : `From $${fc.priceTiers[0]?.price}`) : 'not found';
          console.info(`[GameFix Services] source=google_sheet(synced) | PS5 Fan Connector id=${fc?.id} price=${price}`);
        }
      }).catch(() => {
        if (import.meta.env.DEV) {
          const fc = findFanConn(allServices);
          const price = fc ? (fc.basePrice !== null ? `$${fc.basePrice}` : `From $${fc.priceTiers[0]?.price}`) : 'not found';
          console.warn(`[GameFix Services] source=fallback(sync failed) | PS5 Fan Connector id=${fc?.id} price=${price}`);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stale cart protection: after services reload from sheet, strip any cart items
  // that the sheet now marks as add-ons (requiresParentService / isUpsell / add_on type).
  useEffect(() => {
    if (allServices.length === 0) return;
    setCart(prev => {
      const filtered = prev.filter(item => {
        const fresh = allServices.find(s => s.id === item.service.id);
        if (!fresh) return true; // keep synthetic/seed services (e.g. 'other-inquiry')
        return !fresh.requiresParentService && !fresh.isUpsell && fresh.serviceType !== 'add_on';
      });
      return filtered.length !== prev.length ? filtered : prev;
    });
  }, [allServices]);

  // Primary service revalidation: if the cart no longer contains any primary service,
  // clear all add-ons — an add-on must never survive alone in the booking.
  useEffect(() => {
    const hasPrimary = cart.some(
      i => !i.service.requiresParentService && !i.service.isUpsell && i.service.serviceType !== 'add_on'
    );
    if (!hasPrimary) setAddons([]);
  }, [cart]); // intentionally excludes addons to avoid loop

  const categoryModels = useMemo<Map<DeviceCategory, string[]>>(() => {
    const map = new Map<DeviceCategory, string[]>();

    // Seed from stable config first — these model IDs are always present
    for (const [cat, models] of Object.entries(STABLE_DEVICE_MODELS)) {
      map.set(cat as DeviceCategory, models.map(m => m.id));
    }

    // Supplement with sheet-derived models for non-stable categories (phones etc.)
    // For stable categories, sheet can add new models but never removes existing ones.
    for (const svc of allServices) {
      if (svc.deviceModels.length === 0) continue;
      const stableModels = getStableModels(svc.deviceCategory);
      if (stableModels) {
        // Stable category: add sheet models not already in the stable list
        const existing = map.get(svc.deviceCategory)!;
        for (const m of svc.deviceModels) {
          const slug = m.toLowerCase();
          if (!existing.includes(slug)) existing.push(slug);
        }
      } else {
        // Non-stable category: derive model list from sheet (phones, etc.)
        const existing = map.get(svc.deviceCategory);
        if (!existing) {
          map.set(svc.deviceCategory, [...svc.deviceModels]);
        } else {
          for (const m of svc.deviceModels) {
            if (!existing.includes(m)) existing.push(m);
          }
        }
      }
    }

    return map;
  }, [allServices]);

  // Two-level handheld-pc navigation: family (subcategory) → models (compatible_models).
  // Entirely sheet-driven — no hardcoded lists. Order reflects first appearance in services array.
  const handheldFamilyMap = useMemo((): { family: string; models: string[] }[] => {
    const familyOrder: string[] = [];
    const familyModelMap = new Map<string, Map<string, string>>(); // family → lc-key → original
    for (const svc of allServices) {
      if (svc.deviceCategory !== 'handheld-pc' || !svc.tags[0]) continue;
      const family = svc.tags[0];
      if (!familyModelMap.has(family)) {
        familyModelMap.set(family, new Map());
        familyOrder.push(family);
      }
      const modelMap = familyModelMap.get(family)!;
      for (const m of svc.deviceModels) {
        const key = m.toLowerCase();
        if (!modelMap.has(key)) modelMap.set(key, m);
      }
    }
    return familyOrder.map(family => ({
      family,
      models: [...(familyModelMap.get(family)?.values() ?? [])],
    }));
  }, [allServices]);

  const activeCategoryOptions = segment === 'phone' ? PHONE_CATEGORY_OPTIONS : CATEGORY_OPTIONS;

  const availableCategories = useMemo(
    () => activeCategoryOptions.filter(c =>
      c.id === 'other' ||
      hasStableModels(c.id) ||
      allServices.some(s => s.deviceCategory === c.id)
    ),
    [allServices, activeCategoryOptions],
  );

  const filteredServices = useMemo(() => {
    let list = allServices.filter(s =>
      !CLEANING_ADDON_IDS.has(s.id) &&
      !s.isUpsell &&
      !s.requiresParentService &&
      s.serviceType !== 'add_on'
    );
    if (selectedCategory) list = list.filter(s => s.deviceCategory === selectedCategory);
    if (selectedModelId) {
      const modelLower = selectedModelId.toLowerCase();
      list = list.filter(s =>
        s.deviceModels.length === 0 ||
        s.deviceModels.some(m => m.toLowerCase() === modelLower),
      );
    }
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      list = list.filter(s =>
        s.repairType.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.tags.some(t => t.includes(q)) ||
        s.deviceModels.some(m => m.toLowerCase().includes(q))
      );
    }
    return list;
  }, [allServices, selectedCategory, selectedModelId, searchQ]);

  // ── Bundle categories enriched with sheet prices ──────────────────────────
  // Sheet price is authoritative; hardcoded startingAt/originalPrice are fallbacks only.
  const enrichedBuildCategories = useMemo(() =>
    CONTROLLER_BUILD_CATEGORIES.map(cat => {
      let effectivePrice: number | null = null;
      if (cat.serviceId) {
        const svc = allServices.find(s => s.id === cat.serviceId);
        if (svc) effectivePrice = getDisplayPrice(svc);
      }
      return { ...cat, effectivePrice };
    }),
    [allServices],
  );

  const enrichedTmrOptions = useMemo(() =>
    TMR_OPTIONS.map(opt => {
      let effectiveOriginalPrice = opt.originalPrice;
      if (opt.serviceId) {
        const svc = allServices.find(s => s.id === opt.serviceId);
        if (svc) {
          const p = getDisplayPrice(svc);
          if (p !== null) effectiveOriginalPrice = p;
        }
      }
      return { ...opt, effectiveOriginalPrice };
    }),
    [allServices],
  );

  // ── Cart-derived flags ────────────────────────────────────────────────────
  const hasPS5 = useMemo(() =>
    cart.some(i => i.service.deviceCategory === 'ps5'),
    [cart],
  );
  const hasXbox = useMemo(() =>
    cart.some(i => i.service.deviceCategory === 'xbox'),
    [cart],
  );
  const hasPS5Xbox = useMemo(() => hasPS5 || hasXbox, [hasPS5, hasXbox]);
  const xboxOnly   = useMemo(() => hasXbox && !hasPS5, [hasXbox, hasPS5]);
  const hasSwitch = useMemo(() =>
    cart.some(i => i.service.deviceCategory === 'switch'),
    [cart],
  );
  // True when a cleaning-type repair is already in the main service cart.
  const cartHasCleaning = useMemo(() =>
    cart.some(i => /clean|overheating|thermal/i.test(i.service.id + ' ' + i.service.repairType)),
    [cart],
  );

  const hasController = useMemo(() =>
    cart.some(i => i.service.deviceCategory === 'controller'),
    [cart],
  );

  const hasPhone = useMemo(() =>
    cart.some(i => ['iphone', 'ipad', 'samsung'].includes(i.service.deviceCategory)),
    [cart],
  );

  // $30 one-time cleaning add-ons.
  // Sheet is the source of truth for name/price when synced; fallbacks ensure
  // they always appear even before the first sync or on non-sheet builds.
  const consoleCleanAddons = useMemo((): AddonItem[] => {
    const items: AddonItem[] = [];
    if (hasPS5) {
      const svc = allServices.find(s => s.id === 'ps5_overheat_bundle_addon');
      items.push(svc ? serviceToAddon(svc) : {
        id: 'ps5_overheat_bundle_addon',
        name: 'PS5 Overheating & Cleaning — Bundle Add-On',
        description: 'Internal cleaning done during your current repair: fan, heatsink, dust removal, and liquid metal inspection.',
        price: 30,
      });
    }
    if (hasXbox) {
      const svc = allServices.find(s => s.id === 'xbox_overheat_bundle_addon');
      items.push(svc ? serviceToAddon(svc) : {
        id: 'xbox_overheat_bundle_addon',
        name: 'Xbox Overheating & Cleaning — Bundle Add-On',
        description: 'Internal cleaning done during your current repair: fan, heatsink, and dust removal.',
        price: 30,
      });
    }
    return items;
  }, [allServices, hasPS5, hasXbox]);

  // $75 annual cleaning plans — fallbacks mirror the sheet values.
  const annualCleanPlans = useMemo((): AddonItem[] => {
    const items: AddonItem[] = [];
    if (hasPS5) {
      const svc = allServices.find(s => s.id === 'ps5_annual_cleaning_plan');
      items.push(svc ? serviceToAddon(svc) : {
        id: 'ps5_annual_cleaning_plan',
        name: 'PS5 Annual Cleaning Plan',
        description: '2 maintenance cleanings over 12 months. Helps prevent overheating and reduces fan noise.',
        price: 75,
      });
    }
    if (hasXbox) {
      const svc = allServices.find(s => s.id === 'xbox_annual_cleaning_plan');
      items.push(svc ? serviceToAddon(svc) : {
        id: 'xbox_annual_cleaning_plan',
        name: 'Xbox Annual Cleaning Plan',
        description: '2 maintenance cleanings over 12 months. Helps prevent overheating and reduces fan noise.',
        price: 75,
      });
    }
    return items;
  }, [allServices, hasPS5, hasXbox]);

  // IDs of services in the cart that are primary repairs (not add-ons).
  const primaryServiceIds = useMemo(() =>
    new Set(
      cart
        .filter(i => !i.service.requiresParentService && !i.service.isUpsell && i.service.serviceType !== 'add_on')
        .map(i => i.service.id)
    ),
    [cart]
  );

  // Sheet-driven add-ons: active services that require a parent AND whose
  // eligible_parent_service_ids includes the current primary service.
  // These supplement the hardcoded curated add-ons below.
  const sheetEligibleAddons = useMemo((): AddonItem[] => {
    if (primaryServiceIds.size === 0) return [];
    const modelLower = selectedModelId?.toLowerCase();
    return allServices
      .filter(s =>
        s.requiresParentService === true &&
        s.status === 'active' &&
        !CLEANING_ADDON_IDS.has(s.id) && // hardcoded cleaning add-ons handled separately
        [...primaryServiceIds].some(pid => (s.eligibleParentServiceIds ?? []).includes(pid)) &&
        (s.deviceModels.length === 0 || !modelLower || s.deviceModels.some(m => m.toLowerCase() === modelLower))
      )
      .map(serviceToAddon);
  }, [allServices, primaryServiceIds, selectedModelId]);

  // Sheet-driven Nintendo Switch/Switch 2 add-ons filtered by exact model match.
  // Uses deviceModels from the sheet — eligible_parent_service_ids is not in this sheet,
  // so sheetEligibleAddons will always return [] for Nintendo. This memo fills that gap.
  const switchSheetAddons = useMemo((): AddonItem[] => {
    if (!hasSwitch) return [];
    const switchModels = new Set(
      cart.flatMap(item =>
        item.service.deviceCategory === 'switch'
          ? item.service.deviceModels.map(m => m.toLowerCase())
          : []
      )
    );
    return allServices
      .filter(s =>
        s.deviceCategory === 'switch' &&
        s.status === 'active' &&
        (s.serviceType === 'add_on' || s.requiresParentService === true) &&
        (s.eligibleParentServiceIds ?? []).length === 0 &&
        (s.deviceModels.length === 0 || s.deviceModels.some(m => switchModels.has(m.toLowerCase())))
      )
      .map(serviceToAddon);
  }, [allServices, hasSwitch, cart]);

  // Curated add-ons relevant to this cart (annual plan handled separately as featured card)
  const relevantAddons = useMemo((): AddonItem[] => {
    let base: AddonItem[] = [];
    if (hasPhone) {
      base = [ADDON_TEMPERED_GLASS, ADDON_PRIVACY_GLASS, ADDON_PROT_PRO_ULTRA, ADDON_PROT_PRO_MATTE];
    } else if (hasPS5Xbox) {
      // Only show the $30 one-time cleaning if the repair doesn't already include one.
      const cleanItems = cartHasCleaning ? [] : consoleCleanAddons;
      base = [...cleanItems, ADDON_HDMI_CABLE, ADDON_HDMI_BREAKAWAY];
    } else if (hasSwitch) {
      base = switchSheetAddons;
    }
    // Merge sheet-driven add-ons, skipping any whose ID is already in the curated list
    const existingIds = new Set(base.map(a => a.id));
    const sheetExtras = sheetEligibleAddons.filter(a => !existingIds.has(a.id));
    return [...base, ...sheetExtras];
  }, [hasPhone, hasPS5Xbox, hasSwitch, cartHasCleaning, consoleCleanAddons, sheetEligibleAddons, switchSheetAddons]);

  const hasScreenRepair = useMemo(() =>
    cart.some(i => /screen|display|lcd|oled/i.test(i.service.repairType)),
    [cart],
  );

  const showControllerStep = (hasPS5Xbox || hasController) && segment !== 'phone';

  const isOtherCategory = selectedCategory === 'other';

  const activeSteps = useMemo((): { id: StepId; label: string }[] => [
    { id: 'services',   label: 'Services'          },
    ...(isOtherCategory ? [] : [{ id: 'addons' as StepId, label: 'Add-ons' }]),
    ...(showControllerStep && !isOtherCategory ? [{ id: 'controller' as StepId, label: 'Controller Bundle' }] : []),
    { id: 'contact',    label: 'Contact Info'      },
    { id: 'review',     label: 'Review'            },
  ], [showControllerStep, isOtherCategory]);

  // ── What to bring ─────────────────────────────────────────────────────────
  const whatToBringList = useMemo(() => combineWhatToBring(cart), [cart]);

  // ── Cart helpers ──────────────────────────────────────────────────────────
  const cartQty = (id: string) => cart.find(i => i.service.id === id)?.quantity ?? 0;
  const inCart  = (id: string) => cartQty(id) > 0;

  const addToCart = (service: ServiceEntry) =>
    setCart(prev => {
      const existing = prev.find(i => i.service.id === service.id);
      if (existing) return prev.map(i => i.service.id === service.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { service, quantity: 1, selectedModelId }];
    });

  const setCartQty = (id: string, qty: number) => {
    if (qty <= 0) setCart(prev => prev.filter(i => i.service.id !== id));
    else setCart(prev => prev.map(i => i.service.id === id ? { ...i, quantity: qty } : i));
  };

  const removeFromCart = (id: string) => setCart(prev => prev.filter(i => i.service.id !== id));

  // Add-on helpers
  const addonSelected = (id: string) => addons.some(a => a.id === id);
  const toggleAddon   = (item: AddonItem) =>
    setAddons(prev => prev.some(a => a.id === item.id) ? prev.filter(a => a.id !== item.id) : [...prev, item]);
  const removeAddon   = (id: string) => setAddons(prev => prev.filter(a => a.id !== id));

  // When adding an annual plan, auto-add the matching $30 one-time cleaning if
  // no initial cleaning is already covered (repair or addon).
  const handleAnnualPlanToggle = (plan: AddonItem) => {
    const isSelected = addonSelected(plan.id);
    if (!isSelected) {
      const matchId = plan.id.startsWith('ps5') ? 'ps5_overheat_bundle_addon' : 'xbox_overheat_bundle_addon';
      const alreadyCovered = cartHasCleaning || addonSelected(matchId);
      if (!alreadyCovered) {
        const cleanAddon = consoleCleanAddons.find(c => c.id === matchId);
        if (cleanAddon) setAddons(prev => [...prev, cleanAddon]);
      }
    }
    toggleAddon(plan);
  };

  // Model change protection: when the Switch model changes (cart update), drop any
  // selected Switch add-ons that are no longer compatible and show a banner.
  const addonsRef = React.useRef(addons);
  addonsRef.current = addons;
  React.useEffect(() => {
    if (!hasSwitch) return;
    const availableIds = new Set(switchSheetAddons.map(a => a.id));
    const removed = addonsRef.current.filter(
      a => SWITCH_ADDON_GROUP[a.id] !== undefined && !availableIds.has(a.id)
    );
    if (removed.length > 0) {
      setAddons(prev => prev.filter(a => !removed.some(r => r.id === a.id)));
      setRemovedSwitchAddonNames(removed.map(a => a.name));
    }
  }, [switchSheetAddons, hasSwitch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bundle helpers — TMR: only one TMR item at a time
  const bundleSelected   = (id: string) => bundleItems.some(b => b.id === id);
  const removeBundle     = (id: string) => setBundleItems(prev => prev.filter(b => b.id !== id));

  const selectTmrOption = (opt: typeof enrichedTmrOptions[number]) => {
    const finalPrice = Math.round(opt.effectiveOriginalPrice * (1 - opt.discountPct / 100) * 100) / 100;
    const item: BundleItem = {
      id: opt.id, name: opt.name, description: opt.description,
      originalPrice: opt.effectiveOriginalPrice, discountPct: opt.discountPct, finalPrice, type: 'tmr',
    };
    // Replace any existing TMR item
    setBundleItems(prev => [...prev.filter(b => b.type !== 'tmr'), item]);
  };

  const deselectTmr = () => setBundleItems(prev => prev.filter(b => b.type !== 'tmr'));

  const toggleBuildCategory = (cat: typeof enrichedBuildCategories[number]) => {
    if (bundleSelected(cat.id)) {
      removeBundle(cat.id);
    } else {
      const item: BundleItem = {
        id: cat.id, name: cat.name, description: cat.benefit,
        originalPrice: cat.effectivePrice,
        discountPct: 10,
        finalPrice: cat.effectivePrice !== null ? Math.round(cat.effectivePrice * 0.9 * 100) / 100 : null,
        type: 'build',
      };
      setBundleItems(prev => [...prev, item]);
    }
  };

  // ── Buyback flow helpers ──────────────────────────────────────────────────
  const bbCategory = INLINE_BUYBACK_CATS.find(c => c.id === bbCatId) ?? null;
  const bbModel    = bbCategory?.models.find(m => m.id === bbModelId) ?? null;
  const bbOffer    = (bbCategory && bbModelId && bbCond)
    ? calculateOffer(bbCatId!, bbModelId, bbCond, bbICloud ?? undefined, bbCarrier ?? undefined)
    : 0;

  const bbReset = () => {
    setBbOpen(false); setBbStep('category'); setBbCatId(null);
    setBbModelId(null); setBbCond(null); setBbICloud(null); setBbCarrier(null);
  };

  const bbSelectCat = (catId: string) => { setBbCatId(catId); setBbModelId(null); setBbCond(null); setBbICloud(null); setBbCarrier(null); setBbStep('model'); };
  const bbSelectModel = (modelId: string) => { setBbModelId(modelId); setBbCond(null); setBbICloud(null); setBbCarrier(null); setBbStep('condition'); };
  const bbSelectCond = (c: BuybackCondition) => {
    setBbCond(c);
    const cat = INLINE_BUYBACK_CATS.find(x => x.id === bbCatId);
    if (cat?.needsAccountCheck) { setBbStep('icloud'); }
    else if (cat?.needsCarrierCheck) { setBbStep('carrier'); }
    else { setBbStep('offer'); }
  };
  const bbAnswerICloud = (v: boolean) => {
    setBbICloud(v);
    const cat = INLINE_BUYBACK_CATS.find(x => x.id === bbCatId);
    if (cat?.needsCarrierCheck) { setBbStep('carrier'); }
    else { setBbStep('offer'); }
  };
  const bbAnswerCarrier = (v: boolean) => { setBbCarrier(v); setBbStep('offer'); };

  const confirmBuyback = () => {
    if (!bbCategory || !bbModel || !bbCond) return;
    const condLabel = CONDITIONS.find(c => c.id === bbCond)!.label;
    setBuybackItems(prev => [...prev, {
      id: `bb-${Date.now()}`,
      categoryId: bbCatId!, categoryName: bbCategory.name, emoji: bbCategory.emoji,
      modelId: bbModelId!, modelName: bbModel.name,
      condition: bbCond, conditionLabel: condLabel,
      iCloudUnlocked: bbICloud, blacklistClean: bbCarrier,
      offerValue: bbOffer,
    }]);
    bbReset();
  };

  const removeBuyback = (id: string) => setBuybackItems(prev => prev.filter(b => b.id !== id));

  // ── Category / model navigation ───────────────────────────────────────────
  const handleSelectCategory = (cat: DeviceCategory) => {
    setSelectedCategory(cat);
    setSelectedModelId(null);
    setSelectedHandheldFamily(null);
    setSearchQ('');
    if (cat === 'other') {
      setServiceSubStep('other-form');
    } else if (cat === 'controller') {
      setServiceSubStep('ctrl-type');
      setCtrlBrandOpen(false);
    } else if (cat === 'handheld-pc') {
      setServiceSubStep('handheld-family');
    } else if (hasStableModels(cat)) {
      setServiceSubStep('model');
    } else {
      const models = categoryModels.get(cat) ?? [];
      setServiceSubStep(models.length > 1 ? 'model' : 'list');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectFamily = (family: string) => {
    setSelectedHandheldFamily(family);
    setSelectedModelId(null);
    setServiceSubStep('model');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectModel = (modelId: string) => {
    setSelectedModelId(modelId);
    setServiceSubStep('list');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeSelection = () => {
    if (selectedCategory === 'controller') {
      setServiceSubStep('ctrl-type');
      setSelectedModelId(null);
      setCtrlBrandOpen(false);
      return;
    }
    if (selectedCategory === 'handheld-pc') {
      setServiceSubStep('model');
      setSelectedModelId(null);
      return;
    }
    if (selectedCategory && hasStableModels(selectedCategory)) {
      setServiceSubStep('model');
      setSelectedModelId(null);
      return;
    }
    const models = selectedCategory ? (categoryModels.get(selectedCategory) ?? []) : [];
    if (models.length > 1) {
      setServiceSubStep('model');
      setSelectedModelId(null);
    } else {
      setServiceSubStep('category');
      setSelectedCategory(null);
    }
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const handleContinue = () => {
    if (step === 'services') {
      if (serviceSubStep === 'other-form') {
        // Add a synthetic cart item then jump straight to contact
        const syntheticService: import('../types/services').ServiceEntry = {
          id: 'other-inquiry',
          sku: 'OTHER',
          status: 'active',
          deviceCategory: 'other',
          deviceModels: [],
          repairCategory: 'repair',
          // Keep repairType short (device name only) — symptoms go in description
          // so they don't blow RepairShopr's 255-char limit on subject/problem_type
          repairType: otherDevice.trim(),
          description: otherSymptoms.trim(),
          basePrice: null,
          rushPrice: null,
          priceTiers: [],
          turnaround: 'TBD after diagnosis',
          rushTurnaround: '',
          warranty: '',
          mailInAvailable: true,
          internalNotes: '',
          disclaimer: '',
          tags: ['other'],
          imageUrl: '',
          imageAlt: '',
          whatToBring: 'Your device and any relevant accessories',
          upsellIds: [],
          isUpsell: false,
          requiresParentService: false,
          eligibleParentServiceIds: [],
          serviceType: 'repair',
          createdAt: '',
          updatedAt: '',
        };
        setCart([{ service: syntheticService, quantity: 1 }]);
        setStep('contact');
      } else {
        setStep('addons');
      }
    } else if (step === 'addons') {
      setStep(showControllerStep ? 'controller' : 'contact');
    } else if (step === 'controller') {
      setStep('contact');
    } else if (step === 'contact') {
      if (!validateContact()) return;
      setStep('review');
    } else if (step === 'review') {
      handleSubmit();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    if (step === 'services') {
      if (serviceSubStep === 'other-form') {
        setServiceSubStep('category'); setSelectedCategory(null);
        setCart(prev => prev.filter(i => i.service.id !== 'other-inquiry'));
      } else if (serviceSubStep === 'list') {
        if (selectedCategory === 'controller') {
          setServiceSubStep('ctrl-type'); setSelectedModelId(null); setCtrlBrandOpen(false);
        } else if (selectedCategory === 'handheld-pc') {
          setServiceSubStep('model'); setSelectedModelId(null); // back to model picker within same family
        } else if (selectedCategory && hasStableModels(selectedCategory)) {
          setServiceSubStep('model'); setSelectedModelId(null);
        } else {
          const models = selectedCategory ? (categoryModels.get(selectedCategory) ?? []) : [];
          if (models.length > 1) { setServiceSubStep('model'); setSelectedModelId(null); }
          else { setServiceSubStep('category'); setSelectedCategory(null); }
        }
      } else if (serviceSubStep === 'ctrl-type') {
        setServiceSubStep('category'); setSelectedCategory(null); setCtrlBrandOpen(false);
      } else if (serviceSubStep === 'model') {
        if (selectedCategory === 'handheld-pc') {
          setServiceSubStep('handheld-family'); setSelectedHandheldFamily(null);
        } else {
          setServiceSubStep('category'); setSelectedCategory(null);
        }
      } else if (serviceSubStep === 'handheld-family') {
        setServiceSubStep('category'); setSelectedCategory(null);
      } else {
        setLocationId(null);
      }
    } else if (step === 'addons' && segment === 'phone' && !hasPhone && !hasSwitch && !hasPS5Xbox) {
      // On phone segment with no cart — go back to services
      setStep('services');
    } else if (step === 'addons') {
      setStep('services');
    } else if (step === 'controller') {
      setStep('addons');
    } else if (step === 'contact') {
      if (isOtherCategory) {
        setStep('services'); setServiceSubStep('other-form');
      } else {
        setStep(showControllerStep ? 'controller' : 'addons');
      }
    } else if (step === 'review') {
      setStep('contact');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const isMailIn = locationId === 'mail-in';

  const validateContact = (): boolean => {
    const errs: Record<string, string> = {};
    if (!contact.firstName.trim()) errs.firstName = 'First name is required';
    if (!contact.lastName.trim())  errs.lastName  = 'Last name is required';
    if (!contact.phone.trim())     errs.phone     = 'Phone number is required';
    else if (!/[\d\s\-()+]{10,}/.test(contact.phone)) errs.phone = 'Please enter a valid phone number';
    if (!contact.email.trim())     errs.email     = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) errs.email = 'Please enter a valid email address';

    if (isMailIn) {
      if (!shippingAddress.fullName.trim())  errs.shipFullName  = 'Full name is required';
      if (!shippingAddress.address1.trim())  errs.shipAddress1  = 'Address is required';
      if (!shippingAddress.city.trim())      errs.shipCity      = 'City is required';
      if (!shippingAddress.state.trim())     errs.shipState     = 'State is required';
      if (!shippingAddress.zip.trim())       errs.shipZip       = 'ZIP code is required';
      if (!selectedShipping)                 errs.shippingOption = 'Please select a return shipping method';
    } else {
      if (!contact.preferredDate)    errs.preferredDate = 'Please select a preferred date';
      if (!contact.preferredTime)    errs.preferredTime = 'Please select a preferred time';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const setField = <K extends keyof ContactInfo>(key: K, value: ContactInfo[K]) => {
    setContact(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const setShippingField = <K extends keyof ShippingAddress>(key: K, value: ShippingAddress[K]) => {
    setShippingAddress(prev => ({ ...prev, [key]: value }));
    const errKey = `ship${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    setErrors(prev => ({ ...prev, [errKey]: '' }));
  };

  // ── Time slots ────────────────────────────────────────────────────────────
  const { available: availableSlots, booked: bookedSlots, noSlotsToday } = useMemo(
    () => getAvailableTimeSlots(contact.preferredDate),
    [contact.preferredDate],
  );

  useEffect(() => {
    if (contact.preferredTime && !availableSlots.includes(contact.preferredTime)) {
      setContact(prev => ({ ...prev, preferredTime: '' }));
    }
  }, [availableSlots, contact.preferredTime]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!locationId) return;
    const location = LOCATIONS[locationId];
    const payload  = buildPayload(location, cart, addons, bundleItems, contact, buybackItems, shippingAddress, selectedShipping);

    setSubmitting(true);
    setSubmitError(null);
    setRepairshoprFailed(false); // always reset before a fresh attempt
    setDebugInfo(null);

    const debug: Record<string, string> = {
      repairshopr_customer: 'pending',
      repairshopr_ticket:   'pending',
      email_customer:       'pending',
      calendar_event:       'pending',
      twilio_internal:      'pending',
      twilio_customer:      'pending',
    };

    console.group('🔧 GameFix — Repair Intake Submitted');
    console.log('Location:', payload.location.location_id);
    console.log('Customer:', payload.customer.first_name, payload.customer.last_name, payload.customer.email, payload.customer.phone);
    console.log('Services:', payload.selected_services.map(s => s.service_name).join(', '));
    console.log('Date/Time:', payload.appointment.preferred_date, '@', payload.appointment.preferred_time);
    console.log('Estimated total: $' + payload.estimate.estimated_total);
    console.log('Full payload:', JSON.stringify(payload, null, 2));
    console.groupEnd();

    // ── RepairShopr: customer + ticket via server (avoids Safari cross-origin issues) ──
    let ticketId: number | undefined;
    let ticketNumber: number | undefined;
    let rsError: string | undefined;
    let rsFieldErrors: Record<string, string[]> | undefined;
    let rsTrace: string[] | undefined;

    try {
      const rsRes = await fetch('/api/create-ticket', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ payload }),
      });

      // iOS Safari throws "The string did not match the expected pattern" when .json()
      // is called on a non-JSON body (e.g. HTML 404 from a static CDN).
      // Read as text first, then parse — gives a real error message on failure.
      const rsText = await rsRes.text();
      type RsData = {
        ok: boolean; ticketId?: number; ticketNumber?: number; customerId?: number;
        error?: string; step?: string;
        fieldErrors?: Record<string, string[]>;
        trace?: string[];
      };
      // Use const via IIFE so the variable is always initialized (avoids Safari TDZ edge cases
      // with uninitialized let in try/catch blocks inside async functions).
      const rsData: RsData = (() => {
        try {
          return JSON.parse(rsText) as RsData;
        } catch {
          throw new Error(`HTTP ${rsRes.status} — server returned non-JSON: ${rsText.slice(0, 300)}`);
        }
      })();

      if (rsData.ok && rsData.ticketId && rsData.ticketNumber) {
        ticketId     = rsData.ticketId;
        ticketNumber = rsData.ticketNumber;
        console.info(`[GameFix RS] ✅ Ticket #${ticketNumber} (id ${ticketId})`);
        debug.repairshopr_customer = `success — id ${rsData.customerId}`;
        debug.repairshopr_ticket   = `success — ticket #${ticketNumber} (id ${ticketId})`;
        setRepairshoprFailed(false);
      } else {
        console.error('[GameFix RS] ❌ create-ticket failed:', rsData);
        if (rsData.trace) {
          console.group('[GameFix RS] server trace');
          rsData.trace.forEach(l => console.log(l));
          console.groupEnd();
        }
        debug.repairshopr_customer = rsData.step === 'customer_create' ? `fail — ${rsData.error}` : 'see ticket error';
        debug.repairshopr_ticket   = `fail (${rsData.step ?? 'unknown'}): ${rsData.error ?? 'no error returned'}`;
        rsError      = rsData.error ?? `step: ${rsData.step ?? 'unknown'}`;
        rsFieldErrors = rsData.fieldErrors;
        rsTrace       = rsData.trace;
        setRepairshoprFailed(true);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[GameFix RS] ❌ /api/create-ticket threw:', msg);
      debug.repairshopr_customer = `threw: ${msg}`;
      debug.repairshopr_ticket   = `threw: ${msg}`;
      rsError = msg;
      setRepairshoprFailed(true);
    }

    // ── Always fire notifications — SMS goes out even if RepairShopr failed ──
    fetch('/api/intake-notify', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ payload, ticketNumber, ticketId, rsError, rsFieldErrors, rsTrace }),
    })
      .then(r => r.json())
      .then(result => {
        const r = result.results ?? {};
        debug.email_customer  = r.email?.success     ? 'success' : r.email?.skipped     ? `skipped — ${r.email?.reason}`     : `fail — ${r.email?.error ?? 'unknown'}`;
        debug.calendar_event  = r.calendar?.success  ? 'success' : r.calendar?.skipped  ? `skipped — ${r.calendar?.reason}`  : `fail — ${r.calendar?.error ?? 'unknown'}`;
        debug.twilio_internal = r.internalSMS?.success ? 'success' : r.internalSMS?.skipped ? `skipped — ${r.internalSMS?.reason}` : `fail — ${r.internalSMS?.error ?? 'unknown'}`;
        debug.twilio_customer = r.customerSMS?.success ? 'success' : r.customerSMS?.skipped ? `skipped — ${r.customerSMS?.reason}` : `fail — ${r.customerSMS?.error ?? 'unknown'}`;
        setDebugInfo({ ...debug });
        if (result.partialFailure) console.warn('[GameFix Notify] Some notifications failed:', r);
        else console.info('[GameFix Notify] ✅ All notifications sent');
      })
      .catch(err => {
        debug.email_customer = debug.calendar_event = debug.twilio_internal = debug.twilio_customer = `notify call failed: ${err.message}`;
        setDebugInfo({ ...debug });
        console.warn('[GameFix Notify] /api/intake-notify call failed (non-blocking):', err);
      });

    setDebugInfo({ ...debug });
    setSubmitting(false);
    setSubmitted(payload);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const todayStr = useMemo(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  }, []);

  // ── Quick-date helpers ────────────────────────────────────────────────────
  /** Returns a YYYY-MM-DD string for today + n days (local time, not UTC). */
  const offsetDate = (n: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  /** Formats a YYYY-MM-DD string as e.g. "Mon, Jun 8" */
  const formatDateLabel = (s: string): string => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Precomputed quick-pick dates (fixed at render, refreshes on page load)
  const quickDates = useMemo(() => {
    const dayAfterVal = offsetDate(2);
    const [y, m, d] = dayAfterVal.split('-').map(Number);
    const dayAfterName = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' });
    return [
      { label: 'Today',       value: offsetDate(0) },
      { label: 'Tomorrow',    value: offsetDate(1) },
      { label: dayAfterName,  value: dayAfterVal   },
    ];
  }, []);

  /** Is today fully booked / after hours? (computed independently of selection) */
  const todayHasNoSlots = useMemo(() => getAvailableTimeSlots(todayStr).noSlotsToday, [todayStr]);

  /** Is the currently selected date one of the three quick-picks? */
  const isQuickDate = quickDates.some(q => q.value === contact.preferredDate);

  // ── Segment pre-step (Gaming vs Phone & Tablet) ───────────────────────────
  if (!segment && !submitted) {
    return (
      <>
        <SEOHead
          title="Book a Repair — GameFix Solutions"
          description="Start a repair request at GameFix Solutions. Gaming consoles, iPhones, Samsung, iPads, MacBooks."
        />
        <div className="min-h-screen bg-[#0D0D0D] flex flex-col">
          <div className="max-w-2xl mx-auto w-full px-4 pt-12 pb-6 text-center">
            <Link to="/" className="text-[#39FF14] text-xs font-semibold uppercase tracking-widest hover:underline">
              ← GameFix Solutions
            </Link>
            <h1 className="text-3xl font-black text-[#E0E0E0] mt-4 mb-2">Start a Repair Request</h1>
            <p className="text-[#B0B0B0]">What type of device are you bringing in?</p>
          </div>
          <div className="max-w-2xl mx-auto w-full px-4 pb-16">
            <div className="grid sm:grid-cols-2 gap-4">
              <button
                onClick={() => setSegment('gaming')}
                className="group bg-gray-900 border border-gray-800 hover:border-[#39FF14] rounded-2xl p-7 text-left transition-all duration-200 hover:bg-gray-900/80 focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
              >
                <div className="text-4xl mb-4">🎮</div>
                <h2 className="font-bold text-[#E0E0E0] text-xl mb-2 group-hover:text-[#39FF14] transition-colors">Gaming Repair</h2>
                <p className="text-sm text-[#B0B0B0] leading-relaxed">PS5 · Xbox · Nintendo Switch · Controllers · Steam Deck · Retro consoles</p>
                <div className="mt-5 text-xs font-semibold text-[#39FF14] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                  Select <ArrowRight className="h-3 w-3" />
                </div>
              </button>

              <button
                onClick={() => setSegment('phone')}
                className="group bg-[#050B14] border border-[#1E3A5F]/60 hover:border-[#0EA5E9]/60 rounded-2xl p-7 text-left transition-all duration-200 hover:bg-[#0A1628] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]"
              >
                <div className="text-4xl mb-4">📱</div>
                <h2 className="font-bold text-[#E0E0E0] text-xl mb-2 group-hover:text-[#0EA5E9] transition-colors">Phone & Tablet Repair</h2>
                <p className="text-sm text-[#B0B0B0] leading-relaxed">iPhone · iPad · Samsung Galaxy · MacBook · Data Recovery · Screen Protection</p>
                <div className="mt-5 text-xs font-semibold text-[#0EA5E9] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                  Select <ArrowRight className="h-3 w-3" />
                </div>
              </button>
            </div>
            <p className="text-center text-xs text-gray-600 mt-8">
              Not sure? Call us:{' '}
              <a href="tel:9512168534" className="text-[#B0B0B0] hover:text-[#39FF14] transition-colors">(951) 216-8534</a>
            </p>
          </div>
        </div>
      </>
    );
  }

  // ── Location pre-step ─────────────────────────────────────────────────────
  if (!locationId && !submitted) {
    return (
      <>
        <SEOHead
          title="Book a Repair — GameFix Solutions"
          description="Start a repair request at GameFix Solutions in San Diego or Temecula, or mail in your device."
        />
        <div className="min-h-screen bg-[#0D0D0D] flex flex-col">
          <div className="max-w-2xl mx-auto w-full px-4 pt-12 pb-6 text-center">
            <button onClick={() => setSegment(null)} className="text-[#39FF14] text-xs font-semibold uppercase tracking-widest hover:underline">
              ← Change Repair Type
            </button>
            <h1 className="text-3xl font-black text-[#E0E0E0] mt-4 mb-2">
              {segment === 'phone' ? 'Phone & Tablet Repair' : 'Gaming Repair'}
            </h1>
            <p className="text-[#B0B0B0]">Choose your location to get started.</p>
          </div>
          <div className="max-w-3xl mx-auto w-full px-4 pb-16">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(Object.values(LOCATIONS) as LocationInfo[]).map(loc => (
                <button
                  key={loc.id}
                  onClick={() => setLocationId(loc.id)}
                  className="group bg-gray-900 border border-gray-800 hover:border-[#39FF14] rounded-2xl p-6 text-left transition-all duration-200 hover:bg-gray-900/80 focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
                >
                  <div className="text-4xl mb-4">{loc.emoji}</div>
                  <h2 className="font-bold text-[#E0E0E0] text-lg mb-1 group-hover:text-[#39FF14] transition-colors">{loc.name}</h2>
                  {loc.id !== 'mail-in' ? (
                    <>
                      <p className="text-sm text-[#B0B0B0] mb-1 flex items-start gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-gray-500 mt-0.5 shrink-0" />{loc.address}
                      </p>
                      <p className="text-sm text-[#B0B0B0] flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 text-gray-500 shrink-0" />{loc.phone}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-[#B0B0B0] mb-1 flex items-center gap-1.5">
                        <Truck className="h-3.5 w-3.5 text-gray-500 shrink-0" /> Ship your device to us
                      </p>
                      <p className="text-sm text-[#B0B0B0] flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 text-gray-500 shrink-0" />{loc.phone}
                      </p>
                    </>
                  )}
                  <div className="mt-4 text-xs font-semibold text-[#39FF14] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    Select <ArrowRight className="h-3 w-3" />
                  </div>
                </button>
              ))}
            </div>
            <p className="text-center text-xs text-gray-600 mt-8">
              Not sure? Call us first:{' '}
              <a href="tel:9512168534" className="text-[#B0B0B0] hover:text-[#39FF14] transition-colors">(951) 216-8534</a>
            </p>
          </div>
        </div>
      </>
    );
  }

  // ── Confirmation screen ────────────────────────────────────────────────────
  if (submitted) {
    const loc       = LOCATIONS[submitted.location.location_id as LocationId];
    const total     = submitted.estimate.estimated_total;
    const hasQ      = hasAnyQuotes(cart);
    const savings   = submitted.estimate.bundle_savings;
    const isMailInConfirm = submitted.appointment.type === 'mail-in';

    return (
      <>
        <SEOHead title="Request Received — GameFix Solutions" description="Your repair request has been received." noIndex />
        <div className="min-h-screen bg-[#0D0D0D] py-12 px-4">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <CheckCircle2 className="h-16 w-16 text-[#39FF14] mx-auto mb-4" />
              <h1 className="text-3xl font-black text-[#E0E0E0] mb-2">
                {isMailInConfirm ? 'Mail-In Request Received!' : 'Request Received!'}
              </h1>
              <p className="text-[#B0B0B0]">
                {isMailInConfirm
                  ? <>We received your mail-in request, <span className="text-[#E0E0E0] font-semibold">{submitted.customer.first_name}</span>. Ship your device to us using the instructions below.</>
                  : <>We received your repair request, <span className="text-[#E0E0E0] font-semibold">{submitted.customer.first_name}</span>. We'll reach out if we need to adjust your drop-off time.</>
                }
              </p>
            </div>

            {/* ── Mail-In Shipping Instructions ─────────────────────────── */}
            {isMailInConfirm && (
              <div className="bg-[#39FF14]/5 border border-[#39FF14]/30 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="h-5 w-5 text-[#39FF14]" />
                  <h2 className="text-lg font-black text-[#E0E0E0]">Mail-In Shipping Instructions</h2>
                </div>

                <div className="mb-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Ship Your Device To:</p>
                  <div className="bg-gray-900 rounded-xl p-4 font-mono text-sm text-[#E0E0E0] space-y-0.5">
                    <p>{MAILIN_SHIP_TO.name}</p>
                    <p>Attn: {MAILIN_SHIP_TO.attn}</p>
                    <p>{MAILIN_SHIP_TO.address1}</p>
                    <p>{MAILIN_SHIP_TO.city}, {MAILIN_SHIP_TO.state} {MAILIN_SHIP_TO.zip}</p>
                  </div>
                </div>

                <div className="mb-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Please Include Inside the Package:</p>
                  <ul className="space-y-1.5">
                    {[
                      'Your full name',
                      'Phone number',
                      'Email address',
                      submitted.repairshopr?.notes?.match(/Ticket #(\d+)/)
                        ? `RepairShopr Ticket #${submitted.repairshopr.notes.match(/Ticket #(\d+)/)![1]}`
                        : 'A note with your repair ticket number (if available)',
                      'Brief description of the issue',
                    ].map(item => (
                      <li key={item} className="text-sm text-[#B0B0B0] flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 text-[#39FF14] mt-0.5 shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Packaging Recommendations:</p>
                  <ul className="space-y-1.5">
                    {[
                      'Use adequate padding (bubble wrap, foam, or packing peanuts)',
                      'Remove unnecessary accessories unless requested',
                      'Include power adapter only if relevant to the issue',
                      'Secure devices properly to prevent shipping damage',
                    ].map(item => (
                      <li key={item} className="text-sm text-[#B0B0B0] flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 text-[#39FF14] mt-0.5 shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                </div>

                {submitted.shipping_option && (
                  <div className="border-t border-gray-700 pt-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Your Return Shipping Method</p>
                    <p className="text-sm text-[#E0E0E0] font-medium">{submitted.shipping_option.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {submitted.shipping_option.display_price} · {submitted.shipping_option.estimated_delivery}
                    </p>
                    <p className="text-xs text-gray-600 mt-2 italic">Return shipping is billed separately after repair completion.</p>
                  </div>
                )}

                {/* TODO: Mail-In Terms & Conditions — placeholder until finalized */}
                <div className="border-t border-gray-700 pt-4 mt-4">
                  <p className="text-xs text-gray-600 italic">Mail-In Terms &amp; Conditions will appear here once finalized.</p>
                </div>
              </div>
            )}

            <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden mb-6">
              {/* Location */}
              <div className="px-6 py-4 border-b border-gray-800">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  {isMailInConfirm ? 'Mail-In Repair' : 'Location'}
                </p>
                <p className="font-semibold text-[#E0E0E0]">{loc.name}</p>
                {!isMailInConfirm && (
                  <>
                    <p className="text-sm text-[#B0B0B0] flex items-center gap-1.5 mt-1"><MapPin className="h-3.5 w-3.5 text-gray-500 shrink-0" />{loc.address}</p>
                    <p className="text-sm text-[#B0B0B0] flex items-center gap-1.5 mt-0.5"><Phone className="h-3.5 w-3.5 text-gray-500 shrink-0" />{loc.phone}</p>
                  </>
                )}
                {isMailInConfirm && submitted.shipping_address && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mt-1">Return address: {submitted.shipping_address.full_name}, {submitted.shipping_address.address1}{submitted.shipping_address.address2 ? ` ${submitted.shipping_address.address2}` : ''}, {submitted.shipping_address.city}, {submitted.shipping_address.state} {submitted.shipping_address.zip}</p>
                  </div>
                )}
              </div>

              {/* Drop-off (local only) */}
              {!isMailInConfirm && (
              <div className="px-6 py-4 border-b border-gray-800">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Preferred Drop-Off</p>
                <p className="font-semibold text-[#E0E0E0] flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  {submitted.appointment.preferred_date} at {submitted.appointment.preferred_time}
                </p>
                <p className="text-xs text-gray-500 mt-1 italic">
                  Most appointments are drop-off. Your selected time is your preferred drop-off time, not a guaranteed completion time.
                </p>
              </div>
              )}

              {/* Services */}
              <div className="px-6 py-4 border-b border-gray-800">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Selected Services</p>
                <div className="space-y-2">
                  {submitted.selected_services.map(svc => (
                    <div key={svc.service_id} className="flex justify-between items-center">
                      <span className="text-sm text-[#E0E0E0]">{svc.service_name} ×{svc.quantity}</span>
                      <span className="text-sm font-semibold text-[#39FF14]">
                        {svc.price !== null ? `$${svc.price * svc.quantity}` : 'Quote'}
                      </span>
                    </div>
                  ))}

                  {submitted.selected_addons.length > 0 && (
                    <>
                      <p className="text-xs text-gray-500 pt-2 font-semibold uppercase tracking-wide">Add-ons</p>
                      {submitted.selected_addons.map(a => (
                        <div key={a.addon_id} className="flex justify-between items-center">
                          <span className="text-sm text-[#B0B0B0]">{a.addon_name}</span>
                          <span className="text-sm text-[#B0B0B0]">{a.price !== null ? `$${a.price}` : 'Quote'}</span>
                        </div>
                      ))}
                    </>
                  )}

                  {submitted.selected_bundle.length > 0 && (
                    <>
                      <p className="text-xs text-[#00BFFF] pt-2 font-semibold uppercase tracking-wide flex items-center gap-1">
                        <Tag className="h-3 w-3" /> Controller Bundle
                      </p>
                      {submitted.selected_bundle.map(b => (
                        <div key={b.bundle_id} className="flex justify-between items-center">
                          <span className="text-sm text-[#00BFFF]">{b.bundle_name}</span>
                          <div className="text-right">
                            {b.original_price !== null && b.final_price !== null && (
                              <span className="text-xs text-gray-500 line-through mr-1">${b.original_price}</span>
                            )}
                            <span className="text-sm font-semibold text-[#00BFFF]">
                              {b.final_price !== null ? `$${b.final_price}` : 'Quote'}
                            </span>
                          </div>
                        </div>
                      ))}
                      {savings > 0 && (
                        <p className="text-xs text-[#39FF14] font-semibold">Bundle savings: ${savings.toFixed(2)}</p>
                      )}
                    </>
                  )}
                </div>

                <div className="flex justify-between items-center pt-3 mt-2 border-t border-gray-800 font-bold text-[#E0E0E0]">
                  <span>Estimated Total</span>
                  <span className="text-[#39FF14]">{hasQ ? `$${total}+` : `$${total}`}</span>
                </div>
                <p className="text-xs text-gray-600 italic mt-2">
                  Final pricing may vary after inspection. We'll confirm before completing any additional work.
                </p>
              </div>

              {/* What to bring */}
              {whatToBringList.length > 0 && (
                <div className="px-6 py-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">What to Bring</p>
                  <ul className="space-y-1">
                    {whatToBringList.map(item => (
                      <li key={item} className="text-sm text-[#B0B0B0] flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 text-[#39FF14] mt-0.5 shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Dev-mode debug panel — never shown in production */}
            {import.meta.env.DEV && debugInfo && (
              <div className="bg-gray-950 border border-gray-700 rounded-xl p-4 mb-6 font-mono text-xs">
                <p className="text-gray-400 font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
                  <span className="text-yellow-400">⚙</span> Debug — Integration Results (dev only)
                </p>
                <div className="space-y-1.5">
                  {Object.entries(debugInfo).map(([key, value]) => {
                    const ok   = value.startsWith('success') || value.startsWith('found') || value.startsWith('created');
                    const skip = value.startsWith('skip') || value.startsWith('pending');
                    return (
                      <div key={key} className="flex items-start gap-2">
                        <span className={`shrink-0 font-bold w-3 ${ok ? 'text-[#39FF14]' : skip ? 'text-gray-500' : 'text-[#FF1744]'}`}>
                          {ok ? '✓' : skip ? '·' : '✗'}
                        </span>
                        <span className="text-gray-400 w-44 shrink-0">{key}</span>
                        <span className={`break-all leading-relaxed ${ok ? 'text-[#39FF14]/80' : skip ? 'text-gray-600' : 'text-[#FF1744]/80'}`}>
                          {value}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/" className="flex-1 text-center py-3 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-800 text-[#E0E0E0] font-semibold text-sm transition-colors">
                ← Back to Home
              </Link>
              <button
                onClick={() => {
                  setSubmitted(null); setLocationId(null); setCart([]); setAddons([]); setBundleItems([]);
                  setContact(BLANK_CONTACT); setShippingAddress(BLANK_SHIPPING); setSelectedShipping(null);
                  setStep('services');
                  setServiceSubStep('category'); setSelectedCategory(null); setSelectedModelId(null); setCtrlBrandOpen(false);
                  setRepairshoprFailed(false); setDebugInfo(null);
                }}
                className="flex-1 py-3 rounded-xl bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold text-sm transition-colors">
                Submit Another Request
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Main intake flow ──────────────────────────────────────────────────────
  const location   = LOCATIONS[locationId!];
  const canContinue = step === 'services'
    ? (serviceSubStep === 'other-form'
        ? otherDevice.trim().length > 0 && otherSymptoms.trim().length > 0
        : cart.length > 0)
    : true;

  return (
    <>
      <SEOHead
        title="Book a Repair — GameFix Solutions"
        description="Select your services and submit a repair request at GameFix Solutions."
        noIndex
      />
      <div className="min-h-screen bg-[#0D0D0D]">

        {/* Top bar */}
        <div className="bg-gray-900/80 border-b border-gray-800 px-4 py-3 flex items-center gap-3 sticky top-0 z-30 backdrop-blur">
          <button onClick={handleBack} className="text-gray-400 hover:text-[#E0E0E0] transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 text-sm text-[#B0B0B0]">
            <MapPin className="h-4 w-4 text-[#39FF14] shrink-0" />
            <span className="font-semibold text-[#E0E0E0]">{location.name}</span>
            <span className="text-gray-600 hidden sm:inline">—</span>
            <span className="hidden sm:inline">{location.phone}</span>
          </div>
          <button
            onClick={() => setSummaryOpen(o => !o)}
            className="ml-auto flex items-center gap-1.5 text-sm text-[#39FF14] font-semibold lg:hidden"
          >
            <ShoppingCart className="h-4 w-4" />
            {cart.length > 0 && (
              <span className="bg-[#39FF14] text-[#0D0D0D] text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {cart.reduce((s, i) => s + i.quantity, 0)}
              </span>
            )}
            {summaryOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {/* Mobile summary drawer */}
        {summaryOpen && (
          <div className="lg:hidden border-b border-gray-800 bg-gray-950 px-4 py-4">
            <RepairSummary
              cart={cart} addons={addons} bundleItems={bundleItems} buybackItems={buybackItems} step={step}
              onRemove={removeFromCart} onQtyChange={setCartQty}
              onRemoveAddon={removeAddon} onRemoveBundle={removeBundle} onRemoveBuyback={removeBuyback}
              onContinue={() => { setSummaryOpen(false); handleContinue(); }}
            />
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 py-8">
          <ProgressBar activeSteps={activeSteps} currentStep={step} />

          {/* ── STEP: Services ────────────────────────────────────────────── */}
          {step === 'services' && (
            <div className="flex gap-6">
              <div className="flex-1 min-w-0">

                {/* Sub-step A: Category */}
                {serviceSubStep === 'category' && (
                  <>
                    <h2 className="text-xl font-black text-[#E0E0E0] mb-1">What are you bringing in?</h2>
                    <p className="text-gray-500 text-xs mb-5">Select the device type to get started.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {availableCategories.map(cat => {
                        const catModels = categoryModels.get(cat.id) ?? [];
                        const modelPreview = cat.id === 'handheld-pc' && handheldFamilyMap.length > 0
                          ? handheldFamilyMap.slice(0, 3).map(f => f.family).join(' · ') + (handheldFamilyMap.length > 3 ? ` +${handheldFamilyMap.length - 3}` : '')
                          : catModels.length > 0
                            ? catModels.slice(0, 3).map(formatModelName).join(' · ') + (catModels.length > 3 ? ` +${catModels.length - 3}` : '')
                            : cat.description;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => handleSelectCategory(cat.id)}
                            className="group bg-gray-900 border border-gray-800 hover:border-[#39FF14] rounded-xl p-4 text-left transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
                          >
                            <div className="text-3xl mb-2.5">{cat.emoji}</div>
                            <h3 className="font-bold text-[#E0E0E0] text-sm leading-snug group-hover:text-[#39FF14] transition-colors">{cat.label}</h3>
                            <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{modelPreview}</p>
                          </button>
                        );
                      })}
                      {/* Always-visible "Other" catch-all */}
                      <button
                        onClick={() => handleSelectCategory('other')}
                        className="group bg-gray-900 border border-gray-800 border-dashed hover:border-[#39FF14] rounded-xl p-4 text-left transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
                      >
                        <div className="text-3xl mb-2.5">🔧</div>
                        <h3 className="font-bold text-[#E0E0E0] text-sm leading-snug group-hover:text-[#39FF14] transition-colors">Something Else</h3>
                        <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">PS3, PS4, Wii, other consoles &amp; devices</p>
                      </button>
                    </div>
                  </>
                )}

                {/* Sub-step: Gaming Handheld family picker (handheld-pc only) */}
                {serviceSubStep === 'handheld-family' && (
                  <>
                    <div className="flex items-center gap-2 mb-5">
                      <button onClick={() => { setServiceSubStep('category'); setSelectedCategory(null); }} className="text-gray-500 hover:text-[#E0E0E0] transition-colors">
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm font-semibold text-[#39FF14]">🖥️ Gaming Handheld</span>
                    </div>
                    <h2 className="text-xl font-black text-[#E0E0E0] mb-1">Which device do you have?</h2>
                    <p className="text-gray-500 text-xs mb-5">Select your device family — we'll show the exact models next.</p>
                    {handheldFamilyMap.length === 0 ? (
                      <div className="text-center py-12 text-gray-600">
                        <Package className="h-8 w-8 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No services listed yet. Contact us for help.</p>
                        <a href="tel:+16194990190" className="inline-block mt-3 text-[#39FF14] text-xs font-semibold hover:underline">(619) 499-0190</a>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {handheldFamilyMap.map(({ family, models }) => (
                          <button
                            key={family}
                            onClick={() => handleSelectFamily(family)}
                            className="group bg-gray-900 border border-gray-800 hover:border-[#39FF14] rounded-xl p-4 text-left transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
                          >
                            <h3 className="font-bold text-[#E0E0E0] text-sm leading-snug group-hover:text-[#39FF14] transition-colors">{family}</h3>
                            {models.length > 0 && (
                              <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                                {models.slice(0, 3).map(m => formatModelName(m)).join(' · ')}{models.length > 3 ? ` +${models.length - 3}` : ''}
                              </p>
                            )}
                            <div className="mt-3 flex items-center gap-1 text-[11px] font-semibold text-[#39FF14] opacity-0 group-hover:opacity-100 transition-opacity">
                              Select model <ArrowRight className="h-3 w-3" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Sub-step B: Model */}
                {serviceSubStep === 'model' && selectedCategory && (() => {
                  const isHandheldPc = selectedCategory === 'handheld-pc';
                  const stableModels = getStableModels(selectedCategory);
                  const modelIds = isHandheldPc
                    ? (handheldFamilyMap.find(f => f.family === selectedHandheldFamily)?.models ?? [])
                    : stableModels
                      ? stableModels.map(m => m.id)
                      : (categoryModels.get(selectedCategory) ?? []);
                  const catOption = CATEGORY_OPTIONS.find(c => c.id === selectedCategory) ?? PHONE_CATEGORY_OPTIONS.find(c => c.id === selectedCategory)!;
                  const backLabel = isHandheldPc && selectedHandheldFamily ? selectedHandheldFamily : catOption.label;
                  const handleBackFromModel = () => {
                    if (isHandheldPc) { setServiceSubStep('handheld-family'); setSelectedHandheldFamily(null); }
                    else { setServiceSubStep('category'); setSelectedCategory(null); }
                  };
                  return (
                    <>
                      <div className="flex items-center gap-2 mb-5">
                        <button onClick={handleBackFromModel} className="text-gray-500 hover:text-[#E0E0E0] transition-colors">
                          <ArrowLeft className="h-4 w-4" />
                        </button>
                        <span className="text-sm font-semibold text-[#39FF14]">{catOption.emoji} {backLabel}</span>
                      </div>
                      <h2 className="text-xl font-black text-[#E0E0E0] mb-1">Which model do you have?</h2>
                      <p className="text-gray-500 text-xs mb-5">We'll show only the services that apply to your specific model.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {modelIds.map(model => {
                          const hasServices = allServices.some(s =>
                            s.deviceCategory === selectedCategory &&
                            (s.deviceModels.length === 0 || s.deviceModels.some(m => m.toLowerCase() === model.toLowerCase()))
                          );
                          return (
                            <button
                              key={model}
                              onClick={() => handleSelectModel(model)}
                              className="group bg-gray-900 border border-gray-800 hover:border-[#39FF14] rounded-xl p-5 text-left transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
                            >
                              <h3 className="font-bold text-[#E0E0E0] text-sm leading-snug group-hover:text-[#39FF14] transition-colors">{formatModelName(model)}</h3>
                              {!hasServices && (
                                <p className="mt-1 text-[10px] text-gray-600">No services listed yet</p>
                              )}
                              <div className="mt-3 flex items-center gap-1 text-[11px] font-semibold text-[#39FF14] opacity-0 group-hover:opacity-100 transition-opacity">
                                View services <ArrowRight className="h-3 w-3" />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}

                {/* Sub-step B1b: Controller type / brand picker */}
                {serviceSubStep === 'ctrl-type' && (
                  <>
                    <div className="flex items-center gap-2 mb-5">
                      <button onClick={() => { setServiceSubStep('category'); setSelectedCategory(null); setCtrlBrandOpen(false); }} className="text-gray-500 hover:text-[#E0E0E0] transition-colors">
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm font-semibold text-[#39FF14]">🎯 Controllers</span>
                    </div>
                    <h2 className="text-xl font-black text-[#E0E0E0] mb-1">What kind of controller?</h2>
                    <p className="text-gray-500 text-xs mb-5">We'll show only the services that apply to your controller.</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                      {CONTROLLER_TYPES.map(type => (
                        <button
                          key={type.id}
                          onClick={() => { setSelectedModelId(type.id); setServiceSubStep('list'); setCtrlBrandOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                          className="group flex items-center gap-3 bg-gray-900 border border-gray-800 hover:border-[#39FF14] rounded-xl px-4 py-3.5 text-left transition-all"
                        >
                          <span className="text-xl shrink-0">{type.emoji}</span>
                          <span className="font-bold text-sm text-[#E0E0E0] group-hover:text-[#39FF14] transition-colors leading-tight">{type.label}</span>
                          <ArrowRight className="h-3.5 w-3.5 text-gray-700 group-hover:text-[#39FF14] ml-auto shrink-0 transition-colors" />
                        </button>
                      ))}
                    </div>

                    {/* Other / Pro Controller — expandable brand list */}
                    <button
                      onClick={() => setCtrlBrandOpen(v => !v)}
                      className="w-full flex items-center gap-3 bg-gray-900 border border-gray-800 hover:border-gray-600 rounded-xl px-4 py-3.5 text-left transition-all"
                    >
                      <span className="text-xl shrink-0">🎯</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-[#E0E0E0] leading-tight">Other / Pro Controller</p>
                        <p className="text-xs text-gray-500 mt-0.5">Battle Beaver, SCUF, AIM, HexGaming…</p>
                      </div>
                      {ctrlBrandOpen
                        ? <ChevronUp className="h-4 w-4 text-gray-500 shrink-0" />
                        : <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                      }
                    </button>

                    {ctrlBrandOpen && (
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {CONTROLLER_BRANDS.map(brand => (
                          <button
                            key={brand.id}
                            onClick={() => { setSelectedModelId(brand.id); setServiceSubStep('list'); setCtrlBrandOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            className="group bg-gray-900 border border-gray-800 hover:border-[#39FF14] rounded-lg px-3 py-2.5 text-left transition-all flex items-center gap-2"
                          >
                            <span className="text-sm">{brand.emoji}</span>
                            <span className="font-semibold text-sm text-[#E0E0E0] group-hover:text-[#39FF14] transition-colors leading-tight">{brand.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Sub-step B2: Other / free-form */}
                {serviceSubStep === 'other-form' && (
                  <>
                    <div className="flex items-center gap-2 mb-5">
                      <button onClick={() => { setServiceSubStep('category'); setSelectedCategory(null); setCart(prev => prev.filter(i => i.service.id !== 'other-inquiry')); }} className="text-gray-500 hover:text-[#E0E0E0] transition-colors">
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm font-semibold text-[#39FF14]">🔧 Other / Custom Device</span>
                    </div>

                    <h2 className="text-xl font-black text-[#E0E0E0] mb-1">Tell us about your device</h2>
                    <p className="text-gray-500 text-xs mb-6">
                      Describe what you're bringing in and what's wrong. We'll reach out to confirm pricing and availability before your drop-off.
                    </p>

                    <div className="space-y-5 max-w-lg">
                      {/* Device name */}
                      <div>
                        <label className="block text-sm font-semibold text-[#E0E0E0] mb-1.5">
                          What device are you bringing in? <span className="text-[#FF1744]">*</span>
                        </label>
                        <input
                          type="text"
                          value={otherDevice}
                          onChange={e => setOtherDevice(e.target.value)}
                          placeholder="e.g. PS4 Pro, Nintendo Wii, PS3 Slim, Xbox 360…"
                          className="w-full bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors placeholder-gray-600"
                        />
                      </div>

                      {/* Symptoms */}
                      <div>
                        <label className="block text-sm font-semibold text-[#E0E0E0] mb-1.5">
                          What's the issue? <span className="text-[#FF1744]">*</span>
                        </label>
                        <textarea
                          value={otherSymptoms}
                          onChange={e => setOtherSymptoms(e.target.value)}
                          placeholder="e.g. Won't turn on, disc not reading, HDMI no signal, overheating and shutting off, controller won't sync…"
                          rows={4}
                          className="w-full bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors placeholder-gray-600 resize-none"
                        />
                        <p className="text-[11px] text-gray-600 mt-1">The more detail, the better — include any error codes or recent events.</p>
                      </div>

                      {/* Info banner */}
                      <div className="bg-[#00BFFF]/5 border border-[#00BFFF]/20 rounded-xl p-4 flex items-start gap-3">
                        <AlertCircle className="h-4 w-4 text-[#00BFFF] shrink-0 mt-0.5" />
                        <p className="text-xs text-[#B0B0B0] leading-relaxed">
                          Pricing for unlisted devices is quoted after a quick diagnosis. We'll confirm the estimate before doing any work — no surprises.
                        </p>
                      </div>

                      {/* Mobile continue */}
                      <div className="lg:hidden pt-2">
                        <button onClick={handleContinue} disabled={!canContinue}
                          className="w-full py-3.5 rounded-xl bg-[#39FF14] hover:bg-[#00BFFF] disabled:bg-gray-800 disabled:text-gray-600 text-[#0D0D0D] font-bold text-sm transition-colors flex items-center justify-center gap-2">
                          Continue to Contact Info <ArrowRight className="h-4 w-4" />
                        </button>
                        {!canContinue && <p className="text-center text-xs text-gray-600 mt-2">Fill in both fields to continue.</p>}
                      </div>
                    </div>
                  </>
                )}

                {/* Sub-step C: Service list */}
                {serviceSubStep === 'list' && (
                  <>
                    <div className="flex items-center gap-2 flex-wrap mb-4">
                      <button onClick={handleBack} className="text-gray-500 hover:text-[#E0E0E0] transition-colors">
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      {selectedCategory && (
                        <div className="flex items-center gap-1.5 text-sm">
                          <span className="text-gray-400">{CATEGORY_OPTIONS.find(c => c.id === selectedCategory)?.emoji}</span>
                          <span className="font-semibold text-[#E0E0E0]">
                            {selectedModelId ? formatModelName(selectedModelId) : CATEGORY_OPTIONS.find(c => c.id === selectedCategory)?.label}
                          </span>
                          <button onClick={handleChangeSelection} className="text-xs text-[#39FF14] hover:underline font-semibold ml-0.5">Change</button>
                        </div>
                      )}
                    </div>

                    <h2 className="text-xl font-black text-[#E0E0E0] mb-0.5">Select Services</h2>
                    <p className="text-gray-500 text-xs mb-4">Tap + Add to select. Tap the service name for details.</p>

                    <div className="sticky top-[57px] z-20 bg-[#0D0D0D] pb-3 pt-1 -mx-4 px-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                        <input
                          value={searchQ}
                          onChange={e => setSearchQ(e.target.value)}
                          placeholder="Search services…"
                          className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-9 pr-8 py-2 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors"
                        />
                        {searchQ && (
                          <button onClick={() => setSearchQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {filteredServices.length === 0 ? (
                      <div className="text-center py-16 text-gray-600">
                        <Package className="h-8 w-8 mx-auto mb-3 opacity-30" />
                        {searchQ ? (
                          <>
                            <p className="text-sm">No services found.</p>
                            <button onClick={() => setSearchQ('')} className="text-[#39FF14] text-xs mt-2 hover:underline">Clear search</button>
                          </>
                        ) : selectedModelId ? (
                          <>
                            <p className="text-sm font-semibold text-[#E0E0E0]">No services listed yet for {formatModelName(selectedModelId)}.</p>
                            <p className="text-xs mt-1">Contact us for help — we may still be able to assist.</p>
                            <a href="tel:+16194990190" className="inline-block mt-3 text-[#39FF14] text-xs font-semibold hover:underline">(619) 499-0190</a>
                          </>
                        ) : (
                          <p className="text-sm">No services found.</p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
                        {filteredServices.map(s => (
                          <ServiceRow
                            key={s.id} service={s} inCart={inCart(s.id)} quantity={cartQty(s.id)}
                            selectedModelId={selectedModelId}
                            onAdd={() => addToCart(s)} onIncrease={() => addToCart(s)}
                            onDecrease={() => setCartQty(s.id, cartQty(s.id) - 1)}
                          />
                        ))}
                      </div>
                    )}

                    <div className="lg:hidden mt-6">
                      <button onClick={handleContinue} disabled={!canContinue}
                        className="w-full py-3.5 rounded-xl bg-[#39FF14] hover:bg-[#00BFFF] disabled:bg-gray-800 disabled:text-gray-600 text-[#0D0D0D] font-bold text-sm transition-colors flex items-center justify-center gap-2">
                        Continue <ArrowRight className="h-4 w-4" />
                      </button>
                      {!canContinue && <p className="text-center text-xs text-gray-600 mt-2">Add at least one service to continue.</p>}
                    </div>
                  </>
                )}
              </div>

              {(serviceSubStep === 'list' || serviceSubStep === 'other-form') && (
                <div className="hidden lg:block w-72 shrink-0">
                  <div className="sticky top-20">
                    {serviceSubStep === 'other-form' ? (
                      <div className="bg-[#111] border border-gray-800 rounded-xl p-5 text-center text-gray-600 text-xs">
                        <FileText className="h-6 w-6 mx-auto mb-2 opacity-25" />
                        <p className="leading-relaxed">Your request details will appear here after you fill in the form.</p>
                        <button
                          onClick={handleContinue}
                          disabled={!canContinue}
                          className="mt-4 w-full py-2.5 rounded-lg bg-[#39FF14] hover:bg-[#00BFFF] disabled:bg-gray-800 disabled:text-gray-600 text-[#0D0D0D] font-bold text-xs transition-colors flex items-center justify-center gap-1.5"
                        >
                          Continue <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                        {!canContinue && <p className="text-[10px] text-gray-600 mt-2">Fill in both fields above to continue.</p>}
                      </div>
                    ) : (
                      <>
                        <RepairSummary
                          cart={cart} addons={addons} bundleItems={bundleItems} buybackItems={buybackItems} step={step}
                          onRemove={removeFromCart} onQtyChange={setCartQty}
                          onRemoveAddon={removeAddon} onRemoveBundle={removeBundle} onRemoveBuyback={removeBuyback}
                          onContinue={handleContinue}
                        />
                        {!canContinue && <p className="text-center text-xs text-gray-600 mt-2">Add at least one service to continue.</p>}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP: Add-ons ─────────────────────────────────────────────── */}
          {step === 'addons' && (
            <div className="flex gap-8">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Star className="h-5 w-5 text-[#39FF14]" />
                  <h2 className="text-2xl font-black text-[#E0E0E0]">
                    {hasPhone ? 'Protect Your Screen' : hasPS5Xbox ? 'Console Protection & Add-ons' : hasSwitch ? 'Nintendo Add-ons' : 'Optional Add-ons'}
                  </h2>
                </div>

                {/* Model-change banner — Switch add-ons removed due to incompatible model */}
                {removedSwitchAddonNames.length > 0 && (
                  <div className="bg-amber-950/40 border border-amber-700/50 rounded-xl px-4 py-3 mb-5 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-amber-400 text-sm font-semibold mb-0.5">Some add-ons were removed</p>
                      <p className="text-amber-300/70 text-xs leading-relaxed">
                        <strong>{removedSwitchAddonNames.join(', ')}</strong> {removedSwitchAddonNames.length === 1 ? 'is' : 'are'} not compatible with your updated device selection and {removedSwitchAddonNames.length === 1 ? 'was' : 'were'} removed.
                      </p>
                    </div>
                    <button onClick={() => setRemovedSwitchAddonNames([])} className="text-amber-500/60 hover:text-amber-400 text-lg leading-none shrink-0 mt-0.5">×</button>
                  </div>
                )}

                {hasPhone && hasScreenRepair ? (
                  <div className="bg-[#0EA5E9]/8 border border-[#0EA5E9]/25 rounded-xl px-4 py-3 mb-6">
                    <p className="text-[#0EA5E9] font-semibold text-sm mb-0.5">Perfect time to add screen protection</p>
                    <p className="text-slate-400 text-xs leading-relaxed">
                      Your screen will be brand new after this repair. Protect your investment before you leave — we'll install it while the device is already here.
                    </p>
                  </div>
                ) : hasPhone ? (
                  <p className="text-[#B0B0B0] text-sm mb-6">
                    Add screen protection while your device is here. All optional — skip if nothing fits.
                  </p>
                ) : hasPS5Xbox ? (
                  <p className="text-[#B0B0B0] text-sm mb-6">
                    Keep your console clean and protected. Add what makes sense while it's already with us.
                  </p>
                ) : hasSwitch ? (
                  <p className="text-[#B0B0B0] text-sm mb-6">
                    Upgrade or protect your Nintendo device while it's already with us. All optional — skip if nothing fits.
                  </p>
                ) : (
                  <p className="text-[#B0B0B0] text-sm mb-8">
                    A few things worth adding while your device is already open. All optional — skip if nothing fits.
                  </p>
                )}

                {/* ── Annual Cleaning Plan — Featured Card (PS5 / Xbox only) ── */}
                {hasPS5Xbox && annualCleanPlans.length > 0 && (
                  <div className="mb-6 space-y-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-amber-500/80">
                      ★ Featured — Annual Maintenance Plan
                    </p>
                    {annualCleanPlans.map(plan => {
                      const selected = addonSelected(plan.id);
                      const matchId = plan.id.startsWith('ps5') ? 'ps5_overheat_bundle_addon' : 'xbox_overheat_bundle_addon';
                      const coverageForThis = cartHasCleaning || addonSelected(matchId);
                      return (
                        <div
                          key={plan.id}
                          className={`rounded-xl border-2 p-5 transition-all ${
                            selected
                              ? 'border-amber-400 bg-amber-400/[0.07]'
                              : 'border-amber-500/40 bg-gray-900/70 hover:border-amber-400/60'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] font-bold text-amber-400/80 uppercase tracking-wide mb-1">
                                Protect Your Console for the Next Year
                              </p>
                              <p className={`text-base font-black leading-tight ${selected ? 'text-amber-300' : 'text-[#E0E0E0]'}`}>
                                {plan.name}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className={`text-2xl font-black tabular-nums ${selected ? 'text-amber-300' : 'text-[#E0E0E0]'}`}>
                                ${plan.price}
                              </p>
                              <p className="text-[10px] text-gray-500">/ 12 months</p>
                            </div>
                          </div>
                          <ul className="space-y-1 mb-4">
                            {[
                              '2 maintenance cleanings over 12 months',
                              'Helps prevent overheating & fan noise',
                              'Thermal paste refresh & dust removal',
                            ].map(f => (
                              <li key={f} className="flex items-center gap-2 text-xs text-gray-400">
                                <span className="text-amber-400 shrink-0">✔</span> {f}
                              </li>
                            ))}
                          </ul>
                          {!selected && !coverageForThis && (
                            <div className="text-xs text-amber-500/80 mb-3 bg-amber-500/8 border border-amber-500/20 rounded-lg px-3 py-2 leading-relaxed">
                              Requires an initial cleaning — we'll add the $30 one-time clean automatically when you select this plan.
                            </div>
                          )}
                          {!selected && coverageForThis && (
                            <p className="text-xs text-[#39FF14]/70 mb-3">✓ Initial cleaning covered — ready to start the plan.</p>
                          )}
                          <button
                            onClick={() => handleAnnualPlanToggle(plan)}
                            className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${
                              selected
                                ? 'bg-amber-400/10 text-amber-300 border border-amber-400/30 hover:bg-[#FF1744]/10 hover:text-[#FF1744] hover:border-[#FF1744]/30'
                                : 'bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-400 hover:text-[#0D0D0D] hover:border-amber-400'
                            }`}
                          >
                            {selected ? '✓ Plan Added — Remove' : '+ Add Annual Plan'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Regular add-ons grid ──────────────────────────────────── */}
                {hasPS5Xbox && annualCleanPlans.length > 0 && relevantAddons.length > 0 && (
                  <div className="flex items-center gap-3 mb-5">
                    <div className="flex-1 h-px bg-gray-800" />
                    <p className="text-[10px] text-gray-600 uppercase tracking-widest font-semibold">Add-on accessories</p>
                    <div className="flex-1 h-px bg-gray-800" />
                  </div>
                )}

                {relevantAddons.length === 0 && (!hasPS5Xbox || annualCleanPlans.length === 0) ? (
                  <div className="text-center py-16 text-gray-600">
                    <Zap className="h-8 w-8 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No add-ons to suggest for your current selection.</p>
                  </div>
                ) : relevantAddons.length > 0 ? (
                  hasSwitch ? (
                    /* Grouped layout for Nintendo Switch add-ons */
                    <div className="space-y-7">
                      {SWITCH_ADDON_GROUP_ORDER.map(groupName => {
                        const groupItems = relevantAddons.filter(a => SWITCH_ADDON_GROUP[a.id] === groupName);
                        if (groupItems.length === 0) return null;
                        return (
                          <div key={groupName}>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-3">{groupName}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {groupItems.map(addon => {
                                const selected = addonSelected(addon.id);
                                return (
                                  <div
                                    key={addon.id}
                                    className={`rounded-xl border p-4 transition-all ${selected ? 'border-[#39FF14] bg-[#39FF14]/[0.05]' : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'}`}
                                  >
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-bold leading-snug ${selected ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>{addon.name}</p>
                                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">{addon.description}</p>
                                      </div>
                                      <p className={`text-sm font-black tabular-nums shrink-0 ${selected ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>
                                        {addon.price !== null ? `$${addon.price}` : 'Quote'}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => toggleAddon(addon)}
                                      className={`w-full py-1.5 rounded-lg text-xs font-bold transition-all ${
                                        selected
                                          ? 'bg-[#FF1744]/10 text-[#FF1744] border border-[#FF1744]/30 hover:bg-[#FF1744]/20'
                                          : 'bg-gray-800 text-[#B0B0B0] border border-gray-700 hover:bg-[#39FF14] hover:text-[#0D0D0D] hover:border-[#39FF14]'
                                      }`}
                                    >
                                      {selected ? '✓ Added — Remove' : '+ Add'}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      {/* Ungrouped sheet extras (fallback for add-ons not in SWITCH_ADDON_GROUP) */}
                      {relevantAddons.filter(a => !SWITCH_ADDON_GROUP[a.id]).map(addon => {
                        const selected = addonSelected(addon.id);
                        return (
                          <div key={addon.id} className={`rounded-xl border p-4 transition-all ${selected ? 'border-[#39FF14] bg-[#39FF14]/[0.05]' : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'}`}>
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-bold leading-snug ${selected ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>{addon.name}</p>
                                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{addon.description}</p>
                              </div>
                              <p className={`text-sm font-black tabular-nums shrink-0 ${selected ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>
                                {addon.price !== null ? `$${addon.price}` : 'Quote'}
                              </p>
                            </div>
                            <button onClick={() => toggleAddon(addon)} className={`w-full py-1.5 rounded-lg text-xs font-bold transition-all ${selected ? 'bg-[#FF1744]/10 text-[#FF1744] border border-[#FF1744]/30 hover:bg-[#FF1744]/20' : 'bg-gray-800 text-[#B0B0B0] border border-gray-700 hover:bg-[#39FF14] hover:text-[#0D0D0D] hover:border-[#39FF14]'}`}>
                              {selected ? '✓ Added — Remove' : '+ Add'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {relevantAddons.map(addon => {
                        const selected = addonSelected(addon.id);
                        return (
                          <div
                            key={addon.id}
                            className={`rounded-xl border p-4 transition-all ${selected ? 'border-[#39FF14] bg-[#39FF14]/[0.05]' : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'}`}
                          >
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-bold leading-snug ${selected ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>{addon.name}</p>
                                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{addon.description}</p>
                              </div>
                              <div className="text-right shrink-0">
                                {addon.originalPrice != null && (
                                  <p className="text-xs text-gray-500 line-through tabular-nums">${addon.originalPrice}</p>
                                )}
                                <p className={`text-sm font-black tabular-nums ${selected ? 'text-[#39FF14]' : addon.originalPrice != null ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>
                                  {addon.price !== null ? `$${addon.price}` : 'Quote'}
                                </p>
                                {addon.originalPrice != null && (
                                  <p className="text-[10px] font-bold text-[#39FF14]/70">bundle deal</p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => toggleAddon(addon)}
                              className={`w-full py-1.5 rounded-lg text-xs font-bold transition-all ${
                                selected
                                  ? 'bg-[#FF1744]/10 text-[#FF1744] border border-[#FF1744]/30 hover:bg-[#FF1744]/20'
                                  : 'bg-gray-800 text-[#B0B0B0] border border-gray-700 hover:bg-[#39FF14] hover:text-[#0D0D0D] hover:border-[#39FF14]'
                              }`}
                            >
                              {selected ? '✓ Added — Remove' : '+ Add'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : null}

                {/* ── Trade-In / Buyback section ────────────────────────── */}
                <div className="mt-10 border-t border-gray-800 pt-8">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-5 w-5 text-[#39FF14]" />
                    <h3 className="text-lg font-black text-[#E0E0E0]">Got a device to sell?</h3>
                  </div>
                  <p className="text-[#B0B0B0] text-sm mb-5">
                    We buy iPhones, iPads, MacBooks, PS5s, and Xbox Series consoles. Get an instant estimate — we'll finalize the offer at drop-off.
                  </p>

                  {/* Confirmed trade-in items */}
                  {buybackItems.map(b => (
                    <div key={b.id} className="bg-[#39FF14]/5 border border-[#39FF14]/25 rounded-xl p-4 mb-3 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-bold text-[#E0E0E0]">{b.emoji} {b.modelName}</p>
                        <p className="text-xs text-gray-500">{b.conditionLabel} condition</p>
                        {b.iCloudUnlocked === false && <p className="text-[10px] text-amber-400 mt-0.5">iCloud locked — offer adjusted</p>}
                        {b.blacklistClean === false && <p className="text-[10px] text-amber-400 mt-0.5">Carrier issue — parts value only</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xl font-black text-[#39FF14]">~${b.offerValue}</p>
                        <button onClick={() => removeBuyback(b.id)} className="text-[10px] text-gray-600 hover:text-[#FF1744] transition-colors mt-0.5">Remove</button>
                      </div>
                    </div>
                  ))}

                  {/* Inline flow */}
                  {!bbOpen ? (
                    <button
                      onClick={() => { setBbOpen(true); setBbStep('category'); }}
                      className="w-full border border-dashed border-gray-700 hover:border-[#39FF14] rounded-xl py-4 text-sm font-semibold text-gray-500 hover:text-[#39FF14] transition-all flex items-center justify-center gap-2"
                    >
                      <DollarSign className="h-4 w-4" />
                      {buybackItems.length > 0 ? 'Add another device' : 'Get an instant trade-in estimate'}
                    </button>
                  ) : (
                    <div className="bg-gray-900/70 border border-gray-800 rounded-xl p-5">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          {bbStep !== 'category' && (
                            <button onClick={() => setBbStep(
                              bbStep === 'model' ? 'category' :
                              bbStep === 'condition' ? 'model' :
                              bbStep === 'icloud' ? 'condition' :
                              bbStep === 'carrier' ? 'icloud' : 'condition'
                            )} className="text-gray-500 hover:text-[#E0E0E0] transition-colors">
                              <ArrowLeft className="h-4 w-4" />
                            </button>
                          )}
                          <span className="text-xs font-bold text-[#39FF14] uppercase tracking-wider">
                            {bbStep === 'category' ? 'What device?' :
                             bbStep === 'model' ? `${bbCategory?.emoji} ${bbCategory?.name} — Select model` :
                             bbStep === 'condition' ? 'What condition?' :
                             bbStep === 'icloud' ? 'iCloud / Apple ID' :
                             bbStep === 'carrier' ? 'Carrier status' : 'Your estimate'}
                          </span>
                        </div>
                        <button onClick={bbReset} className="text-gray-600 hover:text-gray-400 transition-colors" title="Cancel">
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Step: Category */}
                      {bbStep === 'category' && (
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                          {INLINE_BUYBACK_CATS.map(cat => (
                            <button
                              key={cat.id}
                              onClick={() => bbSelectCat(cat.id)}
                              className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-gray-800 hover:bg-gray-700 hover:ring-1 hover:ring-[#39FF14]/50 transition-all text-center"
                            >
                              <span className="text-2xl">{cat.emoji}</span>
                              <span className="text-[11px] font-semibold text-[#E0E0E0] leading-tight">{cat.name}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Step: Model */}
                      {bbStep === 'model' && bbCategory && (
                        <div className="max-h-60 overflow-y-auto space-y-1 pr-1">
                          {bbCategory.models.map(m => (
                            <button
                              key={m.id}
                              onClick={() => bbSelectModel(m.id)}
                              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 hover:ring-1 hover:ring-[#39FF14]/40 transition-all text-left"
                            >
                              <span className="text-sm text-[#E0E0E0] font-medium">{m.name}</span>
                              <span className="text-xs text-gray-500 shrink-0 ml-2">Up to ${m.goodPrice}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Step: Condition */}
                      {bbStep === 'condition' && (
                        <div className="space-y-2">
                          {CONDITIONS.map(c => (
                            <button
                              key={c.id}
                              onClick={() => bbSelectCond(c.id)}
                              className="w-full flex items-start gap-3 px-4 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 hover:ring-1 hover:ring-[#39FF14]/40 transition-all text-left"
                            >
                              <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: c.color }} />
                              <div>
                                <p className="text-sm font-bold text-[#E0E0E0]">{c.label}</p>
                                <p className="text-xs text-gray-500 leading-relaxed mt-0.5">{c.desc}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Step: iCloud */}
                      {bbStep === 'icloud' && (
                        <div className="space-y-3">
                          <p className="text-sm text-[#B0B0B0] leading-relaxed">
                            Is the Apple ID / iCloud account signed out on this device?
                          </p>
                          <p className="text-xs text-gray-600">Devices still linked to an iCloud account can't be resold and are worth significantly less.</p>
                          <div className="grid grid-cols-2 gap-3">
                            <button onClick={() => bbAnswerICloud(true)} className="py-3 rounded-xl bg-gray-800 hover:bg-[#39FF14]/10 hover:ring-1 hover:ring-[#39FF14]/40 transition-all text-sm font-bold text-[#E0E0E0]">
                              ✓ Yes, signed out
                            </button>
                            <button onClick={() => bbAnswerICloud(false)} className="py-3 rounded-xl bg-gray-800 hover:bg-gray-700 transition-all text-sm font-bold text-gray-400">
                              ✗ No / Not sure
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step: Carrier */}
                      {bbStep === 'carrier' && (
                        <div className="space-y-3">
                          <p className="text-sm text-[#B0B0B0] leading-relaxed">
                            Is this phone carrier-unlocked or confirmed clean (not blacklisted)?
                          </p>
                          <p className="text-xs text-gray-600">Blacklisted or carrier-locked phones have limited resale value.</p>
                          <div className="grid grid-cols-2 gap-3">
                            <button onClick={() => bbAnswerCarrier(true)} className="py-3 rounded-xl bg-gray-800 hover:bg-[#39FF14]/10 hover:ring-1 hover:ring-[#39FF14]/40 transition-all text-sm font-bold text-[#E0E0E0]">
                              ✓ Unlocked / Clean
                            </button>
                            <button onClick={() => bbAnswerCarrier(false)} className="py-3 rounded-xl bg-gray-800 hover:bg-gray-700 transition-all text-sm font-bold text-gray-400">
                              ✗ Locked / Unsure
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step: Offer */}
                      {bbStep === 'offer' && bbModel && bbCond && (
                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-1">{bbCategory?.emoji} {bbModel.name} — {CONDITIONS.find(c => c.id === bbCond)?.label}</p>
                          {bbICloud === false && <p className="text-[10px] text-amber-400 mb-1">iCloud locked — reduced offer</p>}
                          {bbCarrier === false && <p className="text-[10px] text-amber-400 mb-1">Carrier issue — parts value only</p>}
                          <div className="bg-[#0D0D0D] rounded-xl py-5 px-4 mb-4 border border-[#39FF14]/20">
                            <p className="text-xs text-gray-500 mb-1">Estimated Trade-In Value</p>
                            <p className="text-4xl font-black text-[#39FF14]">~${bbOffer}</p>
                            <p className="text-xs text-gray-600 mt-2">Final offer confirmed after inspection at drop-off.</p>
                          </div>
                          <div className="flex gap-3">
                            <button onClick={bbReset} className="flex-1 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 text-[#B0B0B0] font-semibold text-sm transition-colors flex items-center justify-center gap-1.5">
                              <RotateCcw className="h-3.5 w-3.5" /> Try another
                            </button>
                            <button onClick={confirmBuyback} className="flex-1 py-2.5 rounded-xl bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold text-sm transition-colors flex items-center justify-center gap-1.5">
                              <Check className="h-3.5 w-3.5" /> Confirm trade-in
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-8">
                  <button onClick={handleBack} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-800 text-[#E0E0E0] font-semibold text-sm transition-colors">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  <button onClick={handleContinue} className="flex-1 py-3 rounded-xl bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold text-sm transition-colors flex items-center justify-center gap-2">
                    {showControllerStep ? 'Continue to Controller Bundle' : 'Continue to Contact Info'} <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="hidden lg:block w-80 shrink-0">
                <div className="sticky top-20">
                  <RepairSummary
                    cart={cart} addons={addons} bundleItems={bundleItems} buybackItems={buybackItems} step={step}
                    onRemove={removeFromCart} onQtyChange={setCartQty}
                    onRemoveAddon={removeAddon} onRemoveBundle={removeBundle} onRemoveBuyback={removeBuyback}
                    onContinue={handleContinue}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── STEP: Controller Bundle ───────────────────────────────────── */}
          {step === 'controller' && (
            <div className="flex gap-8">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Gamepad2 className="h-5 w-5 text-[#00BFFF]" />
                  <h2 className="text-2xl font-black text-[#E0E0E0]">Bundle with a Controller Upgrade</h2>
                </div>
                <div className="inline-flex items-center gap-1.5 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-3 py-1 mb-6">
                  <Tag className="h-3.5 w-3.5 text-[#39FF14]" />
                  <span className="text-xs font-bold text-[#39FF14]">10% off when bundled with your console repair</span>
                </div>

                {/* Gallery preview — PS5 only (DualSense builds) */}
                {!xboxOnly && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Our Work</p>
                      <Link to="/custom-controllers" className="text-[11px] text-[#00BFFF] hover:underline font-semibold">
                        View full gallery →
                      </Link>
                    </div>
                    <div className="flex gap-2">
                      {controllerGalleryImages.map((src, i) => (
                        <ImageSlot
                          key={i}
                          slotId={`book-ctrl-gallery-${i}`}
                          fallbackSrc={src}
                          fallbackAlt="Custom controller build"
                          wrapperClassName="flex-1 aspect-square rounded-xl overflow-hidden bg-gray-900 border border-gray-800"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Section A — TMR Upgrade */}
                <h3 className="text-sm font-bold text-[#E0E0E0] uppercase tracking-wider mb-3">TMR Joystick Upgrade</h3>
                <p className="text-xs text-gray-500 mb-4">Hall effect technology — permanently eliminates stick drift. No potentiometers, no wear.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {enrichedTmrOptions.map(opt => {
                    const finalPrice = Math.round(opt.effectiveOriginalPrice * (1 - opt.discountPct / 100) * 100) / 100;
                    const savings    = opt.effectiveOriginalPrice - finalPrice;
                    const isTmrSelected = bundleItems.some(b => b.id === opt.id);
                    return (
                      <button
                        key={opt.id}
                        onClick={() => isTmrSelected ? deselectTmr() : selectTmrOption(opt)}
                        className={`rounded-xl border p-5 text-left transition-all ${
                          isTmrSelected
                            ? 'border-[#00BFFF] bg-[#00BFFF]/[0.05]'
                            : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className={`text-sm font-bold ${isTmrSelected ? 'text-[#00BFFF]' : 'text-[#E0E0E0]'}`}>{opt.name}</p>
                            <p className="text-xs text-[#39FF14] font-semibold mt-0.5">{opt.tagline}</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${isTmrSelected ? 'border-[#00BFFF] bg-[#00BFFF]' : 'border-gray-600'}`}>
                            {isTmrSelected && <Check className="h-3 w-3 text-[#0D0D0D]" />}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mb-3 leading-relaxed">{opt.description}</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-black text-[#00BFFF]">${finalPrice}</span>
                          <span className="text-sm text-gray-500 line-through">${opt.effectiveOriginalPrice}</span>
                          <span className="text-xs text-[#39FF14] font-bold">Save ${savings.toFixed(2)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Section B — Custom Build (PS5 only — Xbox TMR-only flow) */}
                {!xboxOnly && (
                  <div className="border-t border-gray-800 pt-6 mb-6">
                    <h3 className="text-sm font-bold text-[#E0E0E0] uppercase tracking-wider mb-1">Custom Controller Build</h3>
                    <p className="text-xs text-gray-500 mb-4">Prices shown with your 10% bundle discount already applied. Select anything you want added.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {enrichedBuildCategories.map(cat => {
                        const selected = bundleSelected(cat.id);
                        return (
                          <button
                            key={cat.id}
                            onClick={() => toggleBuildCategory(cat)}
                            className={`rounded-xl border p-4 text-left transition-all flex items-start gap-3 ${
                              selected
                                ? 'border-[#00BFFF]/60 bg-[#00BFFF]/[0.05]'
                                : 'border-gray-800 bg-gray-900/40 hover:border-gray-700'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${selected ? 'border-[#00BFFF] bg-[#00BFFF]' : 'border-gray-600'}`}>
                              {selected && <Check className="h-3 w-3 text-[#0D0D0D]" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-bold ${selected ? 'text-[#00BFFF]' : 'text-[#E0E0E0]'}`}>{cat.name}</p>
                              <p className="text-xs text-[#39FF14] font-medium mt-0.5">{cat.benefit}</p>
                              <p className="text-[11px] text-gray-500 mt-1 leading-snug">{cat.detail}</p>
                              {cat.effectivePrice !== null ? (
                                <p className="text-xs text-gray-600 mt-1">
                                  From <span className="font-semibold">${Math.round(cat.effectivePrice * 0.9)}</span>
                                  <span className="line-through ml-1">${cat.effectivePrice}</span>
                                </p>
                              ) : (
                                <p className="text-xs text-gray-600 mt-1">Quote after consultation</p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <button onClick={handleBack} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-800 text-[#E0E0E0] font-semibold text-sm transition-colors">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  <button onClick={handleContinue} className="flex-1 py-3 rounded-xl bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold text-sm transition-colors flex items-center justify-center gap-2">
                    {bundleItems.length > 0 ? 'Continue with Bundle' : 'Skip — No Controller Upgrade'} <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="hidden lg:block w-80 shrink-0">
                <div className="sticky top-20">
                  <RepairSummary
                    cart={cart} addons={addons} bundleItems={bundleItems} buybackItems={buybackItems} step={step}
                    onRemove={removeFromCart} onQtyChange={setCartQty}
                    onRemoveAddon={removeAddon} onRemoveBundle={removeBundle} onRemoveBuyback={removeBuyback}
                    onContinue={handleContinue}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── STEP: Contact Info ────────────────────────────────────────── */}
          {step === 'contact' && (
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center gap-2 mb-1">
                <User className="h-5 w-5 text-[#39FF14]" />
                <h2 className="text-2xl font-black text-[#E0E0E0]">
                  {isMailIn ? 'Your Information & Shipping' : 'Your Information'}
                </h2>
              </div>
              {!isMailIn && (
                <p className="text-[#B0B0B0] text-sm mb-8">
                  Most appointments are drop-off appointments. Your selected time is your preferred drop-off time, not a guaranteed completion time.
                </p>
              )}
              {isMailIn && (
                <p className="text-[#B0B0B0] text-sm mb-8">
                  We'll ship your device back to the address you provide below after the repair is complete.
                </p>
              )}

              <div className="space-y-5">
                {/* ── Contact fields (always shown) ────────────────────────── */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">First Name <span className="text-[#FF1744]">*</span></label>
                    <input value={contact.firstName} onChange={e => setField('firstName', e.target.value)} placeholder="John"
                      className={`mt-1 w-full bg-gray-900 border rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none transition-colors ${errors.firstName ? 'border-[#FF1744]' : 'border-gray-800 focus:border-[#39FF14]'}`} />
                    {errors.firstName && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Last Name <span className="text-[#FF1744]">*</span></label>
                    <input value={contact.lastName} onChange={e => setField('lastName', e.target.value)} placeholder="Smith"
                      className={`mt-1 w-full bg-gray-900 border rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none transition-colors ${errors.lastName ? 'border-[#FF1744]' : 'border-gray-800 focus:border-[#39FF14]'}`} />
                    {errors.lastName && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.lastName}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Phone <span className="text-[#FF1744]">*</span></label>
                    <input type="tel" value={contact.phone} onChange={e => setField('phone', e.target.value)} placeholder="(760) 555-0123"
                      className={`mt-1 w-full bg-gray-900 border rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none transition-colors ${errors.phone ? 'border-[#FF1744]' : 'border-gray-800 focus:border-[#39FF14]'}`} />
                    {errors.phone && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Email <span className="text-[#FF1744]">*</span></label>
                    <input type="email" value={contact.email} onChange={e => setField('email', e.target.value)} placeholder="john@example.com"
                      className={`mt-1 w-full bg-gray-900 border rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none transition-colors ${errors.email ? 'border-[#FF1744]' : 'border-gray-800 focus:border-[#39FF14]'}`} />
                    {errors.email && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.email}</p>}
                  </div>
                </div>

                {/* ── Drop-off date + time (local only) ───────────────────── */}
                {!isMailIn && (
                  <>
                    <div>
                      <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">
                        Preferred Drop-Off Date <span className="text-[#FF1744]">*</span>
                      </label>
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {quickDates.map(({ label, value }) => {
                          const isSelected = contact.preferredDate === value;
                          const isAfterHours = label === 'Today' && todayHasNoSlots;
                          return (
                            <button
                              key={value}
                              type="button"
                              onClick={() => { setField('preferredDate', value); setShowDatePicker(false); }}
                              className={`rounded-xl border p-3 text-left transition-all ${
                                isSelected
                                  ? 'border-[#39FF14] bg-[#39FF14]/[0.06]'
                                  : errors.preferredDate
                                  ? 'border-[#FF1744]/60 bg-gray-900 hover:border-[#FF1744]'
                                  : 'border-gray-800 bg-gray-900 hover:border-gray-600'
                              }`}
                            >
                              <p className={`text-sm font-bold leading-tight ${isSelected ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>{label}</p>
                              <p className="text-[11px] text-gray-500 mt-0.5">{formatDateLabel(value)}</p>
                              {isAfterHours && <p className="text-[10px] text-yellow-500 mt-0.5 font-semibold">After hours</p>}
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => setShowDatePicker(v => !v)}
                          className={`rounded-xl border p-3 text-left transition-all ${
                            showDatePicker || (!isQuickDate && contact.preferredDate)
                              ? 'border-[#39FF14] bg-[#39FF14]/[0.06]'
                              : errors.preferredDate
                              ? 'border-[#FF1744]/60 bg-gray-900 hover:border-[#FF1744]'
                              : 'border-gray-800 bg-gray-900 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center gap-1.5">
                            <CalendarDays className={`h-3.5 w-3.5 shrink-0 ${(!isQuickDate && contact.preferredDate) || showDatePicker ? 'text-[#39FF14]' : 'text-gray-500'}`} />
                            <p className={`text-sm font-bold leading-tight ${(!isQuickDate && contact.preferredDate) || showDatePicker ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>
                              {!isQuickDate && contact.preferredDate ? formatDateLabel(contact.preferredDate) : 'Another Date'}
                            </p>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            {!isQuickDate && contact.preferredDate ? contact.preferredDate : 'Pick from calendar'}
                          </p>
                        </button>
                      </div>
                      {showDatePicker && (
                        <InlineCalendar
                          value={!isQuickDate ? contact.preferredDate : ''}
                          min={todayStr}
                          onChange={v => setField('preferredDate', v)}
                          onClose={() => setShowDatePicker(false)}
                        />
                      )}
                      {errors.preferredDate && (
                        <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.preferredDate}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Preferred Drop-Off Time <span className="text-[#FF1744]">*</span></label>
                      {noSlotsToday ? (
                        <div className="mt-2 bg-gray-900 border border-yellow-500/30 rounded-xl p-4 text-center">
                          <p className="text-sm text-yellow-400 font-semibold mb-1">No same-day slots available</p>
                          <p className="text-xs text-[#B0B0B0]">It's too late to schedule today. Please select a future date, or give us a call.</p>
                        </div>
                      ) : (
                        <div className={`mt-2 p-3 rounded-xl border transition-colors ${errors.preferredTime ? 'border-[#FF1744]' : 'border-gray-800'} bg-gray-900`}>
                          <div className="grid grid-cols-4 gap-2 mb-2">
                            {availableSlots.filter(t => t !== FLEXIBLE_SLOT).map(t => (
                              <button key={t} type="button" onClick={() => setField('preferredTime', t)}
                                className={`py-2 rounded-lg text-xs font-semibold transition-all ${contact.preferredTime === t ? 'bg-[#39FF14] text-[#0D0D0D]' : 'bg-gray-800 text-[#B0B0B0] hover:bg-gray-700 hover:text-[#E0E0E0]'}`}>
                                {t}
                              </button>
                            ))}
                            {bookedSlots.map(t => (
                              <div key={t} title="This slot is already booked"
                                className="py-2 rounded-lg text-xs font-semibold text-center text-gray-600 bg-gray-800/40 line-through cursor-not-allowed select-none">
                                {t}
                              </div>
                            ))}
                          </div>
                          {availableSlots.includes(FLEXIBLE_SLOT) && (
                            <button type="button" onClick={() => setField('preferredTime', FLEXIBLE_SLOT)}
                              className={`w-full py-2 rounded-lg text-xs font-semibold transition-all ${contact.preferredTime === FLEXIBLE_SLOT ? 'bg-[#39FF14] text-[#0D0D0D]' : 'bg-gray-800 text-[#B0B0B0] hover:bg-gray-700 hover:text-[#E0E0E0]'}`}>
                              Flexible — drop off anytime during business hours
                            </button>
                          )}
                        </div>
                      )}
                      {errors.preferredTime && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.preferredTime}</p>}
                    </div>
                  </>
                )}

                {/* ── Return shipping address (mail-in only) ───────────────── */}
                {isMailIn && (
                  <>
                    <div className="border-t border-gray-800 pt-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Truck className="h-4 w-4 text-[#39FF14]" />
                        <h3 className="text-sm font-bold text-[#E0E0E0] uppercase tracking-wide">Return Shipping Address</h3>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Full Name <span className="text-[#FF1744]">*</span></label>
                          <input value={shippingAddress.fullName} onChange={e => setShippingField('fullName', e.target.value)} placeholder="John Smith"
                            className={`mt-1 w-full bg-gray-900 border rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none transition-colors ${errors.shipFullName ? 'border-[#FF1744]' : 'border-gray-800 focus:border-[#39FF14]'}`} />
                          {errors.shipFullName && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.shipFullName}</p>}
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Address Line 1 <span className="text-[#FF1744]">*</span></label>
                          <input value={shippingAddress.address1} onChange={e => setShippingField('address1', e.target.value)} placeholder="123 Main St"
                            className={`mt-1 w-full bg-gray-900 border rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none transition-colors ${errors.shipAddress1 ? 'border-[#FF1744]' : 'border-gray-800 focus:border-[#39FF14]'}`} />
                          {errors.shipAddress1 && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.shipAddress1}</p>}
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Address Line 2 <span className="text-gray-600">(optional)</span></label>
                          <input value={shippingAddress.address2} onChange={e => setShippingField('address2', e.target.value)} placeholder="Apt 4B, Suite 200, etc."
                            className="mt-1 w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="col-span-1 sm:col-span-1">
                            <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">City <span className="text-[#FF1744]">*</span></label>
                            <input value={shippingAddress.city} onChange={e => setShippingField('city', e.target.value)} placeholder="San Diego"
                              className={`mt-1 w-full bg-gray-900 border rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none transition-colors ${errors.shipCity ? 'border-[#FF1744]' : 'border-gray-800 focus:border-[#39FF14]'}`} />
                            {errors.shipCity && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.shipCity}</p>}
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">State <span className="text-[#FF1744]">*</span></label>
                            <input value={shippingAddress.state} onChange={e => setShippingField('state', e.target.value.toUpperCase().slice(0, 2))} placeholder="CA" maxLength={2}
                              className={`mt-1 w-full bg-gray-900 border rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none transition-colors ${errors.shipState ? 'border-[#FF1744]' : 'border-gray-800 focus:border-[#39FF14]'}`} />
                            {errors.shipState && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.shipState}</p>}
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">ZIP <span className="text-[#FF1744]">*</span></label>
                            <input value={shippingAddress.zip} onChange={e => setShippingField('zip', e.target.value)} placeholder="92110"
                              className={`mt-1 w-full bg-gray-900 border rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none transition-colors ${errors.shipZip ? 'border-[#FF1744]' : 'border-gray-800 focus:border-[#39FF14]'}`} />
                            {errors.shipZip && <p className="text-xs text-[#FF1744] mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.shipZip}</p>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Return shipping method ────────────────────────────── */}
                    <div className="border-t border-gray-800 pt-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Package className="h-4 w-4 text-[#39FF14]" />
                        <h3 className="text-sm font-bold text-[#E0E0E0] uppercase tracking-wide">Return Shipping Options</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {SHIPPING_OPTIONS.map(opt => {
                          const selected = selectedShipping?.id === opt.id;
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => { setSelectedShipping(opt); setErrors(prev => ({ ...prev, shippingOption: '' })); }}
                              className={`rounded-xl border p-4 text-left transition-all ${
                                selected
                                  ? 'border-[#39FF14] bg-[#39FF14]/[0.06]'
                                  : errors.shippingOption
                                  ? 'border-[#FF1744]/60 bg-gray-900 hover:border-[#FF1744]'
                                  : 'border-gray-800 bg-gray-900 hover:border-gray-600'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <p className={`text-sm font-bold ${selected ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>{opt.name}</p>
                                <p className={`text-sm font-bold ${selected ? 'text-[#39FF14]' : 'text-[#E0E0E0]'}`}>
                                  {opt.displayPrice}
                                </p>
                              </div>
                              <p className="text-xs text-gray-500">{opt.estimatedDelivery}</p>
                            </button>
                          );
                        })}
                      </div>
                      {errors.shippingOption && (
                        <p className="text-xs text-[#FF1744] mt-2 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.shippingOption}</p>
                      )}
                      <p className="text-xs text-gray-600 mt-3">Return shipping is billed separately. We'll confirm the exact cost before shipping your device back.</p>
                    </div>
                  </>
                )}

                <div>
                  <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Device Model <span className="text-gray-600">(optional)</span></label>
                  <input value={contact.deviceModel} onChange={e => setField('deviceModel', e.target.value)}
                    placeholder="e.g. PS5 Digital Edition, Xbox Series X, Switch OLED…"
                    className="mt-1 w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors" />
                </div>

                {whatToBringList.length > 0 && (
                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                    <p className="text-sm font-semibold text-[#E0E0E0] mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#39FF14]" /> {isMailIn ? 'What to Ship' : 'What to Bring'}
                    </p>
                    <ul className="space-y-2">
                      {whatToBringList.map(item => (
                        <li key={item} className="text-sm text-[#B0B0B0] flex items-start gap-2">
                          <Check className="h-3.5 w-3.5 text-[#39FF14] mt-0.5 shrink-0" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <label className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Describe the Issue <span className="text-gray-600">(optional)</span></label>
                  <textarea value={contact.notes} onChange={e => setField('notes', e.target.value)}
                    placeholder="Any details about the issue — when it started, what you've already tried, error messages, etc."
                    rows={4} maxLength={2000}
                    className="mt-1 w-full bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors resize-none" />
                  <p className="text-xs text-gray-600 mt-1 text-right">{contact.notes.length}/2000</p>
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button onClick={handleBack} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-800 text-[#E0E0E0] font-semibold text-sm transition-colors">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button onClick={handleContinue} className="flex-1 py-3 rounded-xl bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold text-sm transition-colors flex items-center justify-center gap-2">
                  Review & Confirm <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: Review ──────────────────────────────────────────────── */}
          {step === 'review' && (
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-black text-[#E0E0E0] mb-1">Review Your Request</h2>
              <p className="text-[#B0B0B0] text-sm mb-8">Please double-check everything before submitting.</p>

              <div className="space-y-4">

                {/* Location + drop-off / shipping info */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <h3 className="font-semibold text-[#E0E0E0] mb-3 flex items-center gap-2">
                    {isMailIn
                      ? <><Truck className="h-4 w-4 text-[#39FF14]" /> Mail-In Details</>
                      : <><MapPin className="h-4 w-4 text-[#39FF14]" /> Location &amp; Drop-Off</>
                    }
                  </h3>
                  {isMailIn ? (
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Return Shipping To</p>
                        <p className="text-[#E0E0E0] font-medium">{shippingAddress.fullName}</p>
                        <p className="text-[#B0B0B0] text-xs mt-0.5">
                          {shippingAddress.address1}{shippingAddress.address2 ? `, ${shippingAddress.address2}` : ''}
                        </p>
                        <p className="text-[#B0B0B0] text-xs">{shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}</p>
                      </div>
                      {selectedShipping && (
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Return Shipping Method</p>
                          <p className="text-[#E0E0E0] font-medium">{selectedShipping.name}</p>
                          <p className="text-[#B0B0B0] text-xs mt-0.5">
                            {selectedShipping.displayPrice} · {selectedShipping.estimatedDelivery}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Location</p>
                        <p className="text-[#E0E0E0] font-medium">{location.name}</p>
                        <p className="text-[#B0B0B0] text-xs mt-0.5">{location.address}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Preferred Drop-Off</p>
                        <p className="text-[#E0E0E0] font-medium">{contact.preferredDate}</p>
                        <p className="text-[#B0B0B0] text-xs mt-0.5">{contact.preferredTime}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact info */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <h3 className="font-semibold text-[#E0E0E0] mb-3 flex items-center gap-2">
                    <User className="h-4 w-4 text-[#39FF14]" /> Your Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Name</p><p className="text-[#E0E0E0]">{contact.firstName} {contact.lastName}</p></div>
                    <div><p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Phone</p><p className="text-[#E0E0E0]">{contact.phone}</p></div>
                    <div className="col-span-2"><p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Email</p><p className="text-[#E0E0E0]">{contact.email}</p></div>
                    {contact.deviceModel && (
                      <div className="col-span-2"><p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Device Model</p><p className="text-[#E0E0E0]">{contact.deviceModel}</p></div>
                    )}
                    {contact.notes && (
                      <div className="col-span-2"><p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Issue Description</p><p className="text-[#B0B0B0] text-xs leading-relaxed">{contact.notes}</p></div>
                    )}
                  </div>
                </div>

                {/* Services + add-ons + bundle + pricing */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <h3 className="font-semibold text-[#E0E0E0] mb-3 flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-[#39FF14]" /> Services & Pricing
                  </h3>
                  <div className="space-y-2 mb-4">
                    {cart.map(item => {
                      const price = getDisplayPrice(item.service, item.selectedModelId);
                      return (
                        <div key={item.service.id} className="flex justify-between items-center text-sm">
                          <span className="text-[#E0E0E0]">{item.service.repairType} ×{item.quantity}</span>
                          <span className="font-semibold text-[#39FF14]">{price !== null ? `$${price * item.quantity}` : 'Quote'}</span>
                        </div>
                      );
                    })}

                    {addons.length > 0 && (
                      <>
                        <p className="text-xs text-gray-500 pt-2 font-semibold uppercase tracking-wide">Add-ons</p>
                        {addons.map(a => (
                          <div key={a.id} className="flex justify-between items-center text-sm">
                            <span className="text-[#B0B0B0]">{a.name}</span>
                            <span className="text-[#B0B0B0]">{a.price !== null ? `$${a.price}` : 'Quote'}</span>
                          </div>
                        ))}
                      </>
                    )}

                    {bundleItems.length > 0 && (
                      <>
                        <p className="text-xs text-[#00BFFF] pt-2 font-semibold uppercase tracking-wide flex items-center gap-1">
                          <Tag className="h-3 w-3" /> Controller Bundle
                        </p>
                        {bundleItems.map(b => (
                          <div key={b.id} className="flex justify-between items-center text-sm">
                            <span className="text-[#00BFFF]">{b.name}</span>
                            <div className="text-right">
                              {b.originalPrice !== null && b.finalPrice !== null && (
                                <span className="text-xs text-gray-500 line-through mr-1">${b.originalPrice}</span>
                              )}
                              <span className="font-semibold text-[#00BFFF]">{b.finalPrice !== null ? `$${b.finalPrice}` : 'Quote'}</span>
                            </div>
                          </div>
                        ))}
                        {calcBundleSavings(bundleItems) > 0 && (
                          <p className="text-xs text-[#39FF14] font-semibold">Bundle savings: ${calcBundleSavings(bundleItems).toFixed(2)}</p>
                        )}
                      </>
                    )}
                  </div>

                  <div className="border-t border-gray-800 pt-3">
                    <div className="flex justify-between items-center font-bold text-[#E0E0E0]">
                      <span>Estimated Total</span>
                      <span className="text-[#39FF14]">
                        {hasAnyQuotes(cart)
                          ? `$${calcSubtotal(cart) + calcAddonSubtotal(addons) + calcBundleSubtotal(bundleItems)}+`
                          : `$${calcSubtotal(cart) + calcAddonSubtotal(addons) + calcBundleSubtotal(bundleItems)}`
                        }
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 italic">Final pricing may vary after inspection. We'll confirm before completing any additional work.</p>
                  </div>
                </div>

                {/* Trade-in summary */}
                {buybackItems.length > 0 && (
                  <div className="bg-[#39FF14]/5 border border-[#39FF14]/25 rounded-xl p-5">
                    <h3 className="font-semibold text-[#E0E0E0] mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-[#39FF14]" /> Trade-In Interests
                    </h3>
                    <div className="space-y-2">
                      {buybackItems.map(b => (
                        <div key={b.id} className="flex justify-between items-center text-sm">
                          <div>
                            <span className="text-[#E0E0E0]">{b.emoji} {b.modelName}</span>
                            <span className="text-gray-500 ml-2 text-xs">({b.conditionLabel})</span>
                          </div>
                          <span className="font-bold text-[#39FF14]">~${b.offerValue}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-3 italic">Estimates are not guaranteed. Final offer confirmed after inspection at drop-off.</p>
                  </div>
                )}

                {/* What to bring */}
                {whatToBringList.length > 0 && (
                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                    <h3 className="font-semibold text-[#E0E0E0] mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#39FF14]" /> What to Bring
                    </h3>
                    <ul className="space-y-2">
                      {whatToBringList.map(item => (
                        <li key={item} className="text-sm text-[#B0B0B0] flex items-start gap-2">
                          <Check className="h-3.5 w-3.5 text-[#39FF14] mt-0.5 shrink-0" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Disclaimer */}
                <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
                  {isMailIn ? (
                    <p className="text-xs text-gray-500 leading-relaxed">
                      By submitting this request you authorize GameFix Solutions to inspect and repair the described issue.
                      This is not a payment — repair cost is confirmed after inspection. Return shipping is billed separately.
                      We'll always contact you before completing any work beyond what was agreed.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 leading-relaxed">
                      By submitting this request you are requesting a drop-off appointment — not placing an order or making a payment.
                      We will contact you to confirm. Pricing estimates may be revised after inspection.
                      We'll always confirm before completing any work beyond what was agreed.
                    </p>
                  )}
                </div>

                {submitError && (
                  <div className="bg-[#FF1744]/10 border border-[#FF1744]/30 rounded-xl p-4">
                    <p className="text-sm text-[#FF1744]">{submitError}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-8">
                <button onClick={handleBack} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-800 text-[#E0E0E0] font-semibold text-sm transition-colors">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button onClick={handleContinue} disabled={submitting}
                  className="flex-1 py-3.5 rounded-xl bg-[#39FF14] hover:bg-[#00BFFF] disabled:bg-gray-700 disabled:text-gray-500 text-[#0D0D0D] font-bold text-sm transition-colors flex items-center justify-center gap-2">
                  {submitting ? 'Submitting…' : <> Submit Request <ArrowRight className="h-4 w-4" /> </>}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Debug panel (?debug=1) ──────────────────────────────────────────── */}
      {debugMode && (() => {
        const sheetCache    = getCachedSheetData();
        const cacheIsFresh  = sheetCache ? isCacheFresh(sheetCache.fetchedAt) : false;
        const lsServices    = (() => { try { return localStorage.getItem('gfs_admin_services_v4'); } catch { return null; } })();
        const lsSheet       = (() => { try { return localStorage.getItem(SHEETS_CACHE_KEY); } catch { return null; } })();
        const svcStats      = getLastServiceStats();

        let serviceSource = 'seed (initial-services.ts)';
        if (sheetCache && cacheIsFresh) serviceSource = 'sheet cache (fresh)';
        else if (sheetCache && !cacheIsFresh) serviceSource = 'sheet cache (stale — using localStorage/seed fallback)';
        else if (lsServices) serviceSource = 'localStorage (gfs_admin_services_v4)';

        const matchedServices = allServices.filter(s => {
          if (selectedCategory && s.deviceCategory !== selectedCategory) return false;
          if (selectedModelId) {
            const ml = selectedModelId.toLowerCase();
            if (s.deviceModels.length > 0 && !s.deviceModels.some(m => m.toLowerCase() === ml)) return false;
          }
          return true;
        });

        return (
          <div className="fixed bottom-4 right-4 z-50 w-80 bg-black/95 border border-yellow-500 rounded-xl text-[11px] font-mono text-yellow-300 shadow-2xl overflow-hidden">
            <div className="bg-yellow-500/20 px-3 py-2 flex items-center justify-between border-b border-yellow-500/40">
              <span className="font-bold text-yellow-400">🔧 Debug Panel</span>
              <span className="text-yellow-600 text-[10px]">?debug=1</span>
            </div>
            <div className="p-3 space-y-1.5 max-h-96 overflow-y-auto">
              <div><span className="text-yellow-600">Category:</span> {selectedCategory ?? '—'}</div>
              <div><span className="text-yellow-600">Model:</span> {selectedModelId ?? '—'}</div>
              <div><span className="text-yellow-600">Stable config:</span> {selectedCategory && hasStableModels(selectedCategory) ? '✅ yes' : '⬜ no (sheet-derived)'}</div>
              {selectedModelId && (
                <div className="border-t border-yellow-800 pt-1.5">
                  <span className="text-yellow-600">Model filter:</span>
                  <div className="mt-0.5 space-y-0.5">
                    <div>Filtered services: {filteredServices.length}</div>
                    {filteredServices.slice(0, 3).map(s => (
                      <div key={s.id} className="text-gray-400 pl-1 truncate">
                        · {s.id} — models: [{s.deviceModels.join(', ') || '*'}]
                      </div>
                    ))}
                    {filteredServices.length > 3 && <div className="text-gray-600 pl-1">…+{filteredServices.length - 3} more</div>}
                  </div>
                </div>
              )}
              <div className="border-t border-yellow-800 pt-1.5">
                <span className="text-yellow-600">Services source:</span>
                <div className="text-yellow-200 mt-0.5">{serviceSource}</div>
              </div>
              <div className="border-t border-yellow-800 pt-1.5">
                <span className="text-yellow-600">Service counts (active):</span>
                <div className="mt-0.5 space-y-0.5">
                  <div>Total: {allServices.length}</div>
                  <div>Controllers: {allServices.filter(s => s.deviceCategory === 'controller').length}</div>
                  <div>PS5 Edge: {allServices.filter(s => s.deviceCategory === 'controller' && (s.id.includes('edge') || s.repairType.toLowerCase().includes('edge'))).length}</div>
                  <div>Current selection: {matchedServices.length}</div>
                </div>
              </div>
              {svcStats && (
                <div className="border-t border-yellow-800 pt-1.5">
                  <span className="text-yellow-600">Merge stats ({svcStats.source}):</span>
                  <div className="mt-0.5 space-y-0.5">
                    {svcStats.source === 'sheet' ? (
                      <>
                        <div>Sheet: {svcStats.sheetCount} ({svcStats.sheetCategories.join(', ')})</div>
                        <div className="text-green-400">+ Seed supplement: {svcStats.seedSupplementCount} ({svcStats.seedSupplementCategories.join(', ') || 'none'})</div>
                        <div className="text-gray-500">— Seed suppressed: {svcStats.seedSkippedCount} ({svcStats.seedSkippedCategories.join(', ') || 'none'})</div>
                        <div className="text-yellow-200">= Active total: {svcStats.totalActive}</div>
                      </>
                    ) : (
                      <div>Seed only: {svcStats.totalActive} active ({svcStats.seedSupplementCategories.join(', ')})</div>
                    )}
                  </div>
                </div>
              )}
              <div className="border-t border-yellow-800 pt-1.5">
                <span className="text-yellow-600">Sheet cache:</span>
                {sheetCache ? (
                  <div className="mt-0.5 space-y-0.5">
                    <div>{cacheIsFresh ? '✅ fresh' : '⚠️ stale'} — {syncAgeLabel(sheetCache.fetchedAt)}</div>
                    <div>{sheetCache.rowCount} rows → {sheetCache.services.length} parsed</div>
                    {sheetCache.skipped && sheetCache.skipped.length > 0 && (() => {
                      const byReason = sheetCache.skipped.reduce<Record<string, number>>((acc, s) => {
                        acc[s.reason] = (acc[s.reason] ?? 0) + 1; return acc;
                      }, {});
                      const unknownCat = sheetCache.skipped.filter(s => s.reason === 'unknown_category');
                      return (
                        <div className="text-orange-400">
                          <div>Skipped: {sheetCache.skipped.length} rows</div>
                          {Object.entries(byReason).map(([r, n]) => (
                            <div key={r} className="pl-2">· {r}: {n}</div>
                          ))}
                          {unknownCat.length > 0 && (
                            <div className="text-red-400 mt-0.5">
                              Unknown cats: {[...new Set(unknownCat.map(s => s.raw))].join(', ')}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : <span className="text-red-400 ml-1">not found</span>}
              </div>
              <div className="border-t border-yellow-800 pt-1.5">
                <span className="text-yellow-600">localStorage keys:</span>
                <div className="mt-0.5 space-y-0.5">
                  <div>gfs_admin_services_v4: {lsServices ? `✅ ${(lsServices.length / 1024).toFixed(1)}kb` : '⬜ not set'}</div>
                  <div>{SHEETS_CACHE_KEY}: {lsSheet ? `✅ ${(lsSheet.length / 1024).toFixed(1)}kb` : '⬜ not set'}</div>
                </div>
              </div>
              {selectedCategory && (
                <div className="border-t border-yellow-800 pt-1.5">
                  <span className="text-yellow-600">Models in picker ({selectedCategory}):</span>
                  <div className="mt-0.5">
                    {(categoryModels.get(selectedCategory) ?? []).map(m => (
                      <div key={m} className="flex items-center gap-1">
                        <span className="text-yellow-500">·</span>
                        <span>{m}</span>
                        {allServices.some(s => s.deviceCategory === selectedCategory && s.deviceModels.some(dm => dm.toLowerCase() === m.toLowerCase())) ? (
                          <span className="text-green-500 ml-auto">has services</span>
                        ) : (
                          <span className="text-gray-600 ml-auto">no services</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default BookRepairPage;
